// Settings — workspace admin area
// Tab structure on the LEFT, content on right (Attio-style settings shell)

// Panes without `adminOnly` are visible to every member. This mirrors (but
// does not enforce) the backend capability registry — the API 403s admin
// routes regardless of what the UI shows (app/permissions.py).
const SETTINGS_NAV = [
  {
    section: 'Workspace',
    items: [
      { id: 'general',     label: 'General',       icon: 'Settings', adminOnly: true },
      { id: 'feature_flags', label: 'Feature flags', icon: 'Flag',     adminOnly: true },
      { id: 'releases',    label: "What's new",    icon: 'Sparkle',  adminOnly: true },
      { id: 'members',     label: 'Members',       icon: 'Users' },
      { id: 'teams',       label: 'Teams',         icon: 'Users', adminOnly: true },
      { id: 'permissions', label: 'Permissions',   icon: 'Check' },
      { id: 'costs',       label: 'Costs',         icon: 'Money', adminOnly: true },
    ],
  },
  {
    section: 'Data model',
    adminOnly: true,
    items: [
      { id: 'objects',     label: 'Objects',       icon: 'Folder' },
      { id: 'industries',  label: 'Industries',    icon: 'Tag' },
      { id: 'lead_sources', label: 'Lead sources',  icon: 'Target' },
      { id: 'tags',        label: 'Custom tags',   icon: 'Tag' },
      { id: 'pipelines',   label: 'Pipelines',     icon: 'Pipeline' },
      { id: 'loi_red_flags', label: 'LOI red flags', icon: 'Flag' },
    ],
  },
  {
    section: 'Automation',
    adminOnly: true,
    items: [
      { id: 'agents',      label: 'Agents',        icon: 'Sparkle' },
      { id: 'bulk_fills',  label: 'Bulk fills',    icon: 'Lightning' },
      { id: 'test_runner', label: 'Test runner',   icon: 'Activity' },
      { id: 'data_quality',label: 'Data quality',  icon: 'Check' },
      { id: 'dc_sync',     label: 'DealCloud sync', icon: 'Activity' },
    ],
  },
  {
    section: 'My account',
    items: [
      { id: 'integrations',label: 'Integrations',  icon: 'External' },
    ],
  },
];

const SettingsView = ({ initialPane, currentRole, selfId } = {}) => {
  const isAdmin = (currentRole || '').toLowerCase() === 'admin';
  const nav = SETTINGS_NAV
    .filter((sec) => isAdmin || !sec.adminOnly)
    .map((sec) => ({ ...sec, items: sec.items.filter((it) => isAdmin || !it.adminOnly) }))
    .filter((sec) => sec.items.length);
  const allowed = new Set(nav.flatMap((sec) => sec.items.map((it) => it.id)));
  const fallback = isAdmin ? 'objects' : 'members';
  const [active, setActive] = React.useState(
    initialPane && allowed.has(initialPane) ? initialPane : (allowed.has(fallback) ? fallback : nav[0]?.items[0]?.id)
  );

  return (
    <div style={{ display: 'flex', height: '100%', background: 'var(--surface-2)' }}>
      {/* Settings sidebar */}
      <aside style={{ width: 224, flexShrink: 0, borderRight: '1px solid var(--border)', background: 'var(--surface)', overflow: 'auto' }}>
        <div style={{ padding: '14px 14px 6px' }}>
          <h2 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>Settings</h2>
          <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 2 }}>IPro Capital · {isAdmin ? 'workspace admin' : 'my settings'}</div>
        </div>
        {nav.map((sec) => (
          <div key={sec.section}>
            <div style={{ padding: '10px 14px 4px', fontSize: 10.5, fontWeight: 600, color: 'var(--text-4)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{sec.section}</div>
            {sec.items.map((it) => {
              const Ic = Icon[it.icon] || Icon.Doc;
              const a = active === it.id;
              return (
                <button key={it.id} onClick={() => setActive(it.id)} style={{
                  display: 'flex', alignItems: 'center', gap: 9, width: '100%', padding: '5px 14px',
                  height: 28, border: 'none', cursor: 'pointer', textAlign: 'left',
                  fontSize: 12.5, fontWeight: a ? 600 : 500,
                  color: a ? 'var(--text)' : 'var(--text-2)',
                  background: a ? 'var(--surface-hover)' : 'transparent',
                  borderLeft: a ? '2px solid var(--accent)' : '2px solid transparent',
                }}>
                  <Ic size={13} color={a ? 'var(--accent)' : 'var(--text-3)'} />
                  {it.label}
                </button>
              );
            })}
          </div>
        ))}
      </aside>

      {/* Settings content */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {active === 'objects'      && <ObjectsSettings />}
        {active === 'pipelines'    && <PipelinesSettings />}
        {active === 'agents'       && <AgentsSettings />}
        {active === 'bulk_fills'   && <BulkFillJobsSettings />}
        {active === 'test_runner'  && <TestRunnerSettings />}
        {active === 'data_quality' && <DataQualitySettings />}
        {active === 'dc_sync'      && <window.DcSyncSettings />}
        {active === 'costs'        && <window.CostsSettings />}
        {active === 'members'      && <MembersSettings isAdmin={isAdmin} selfId={selfId} />}
        {active === 'teams'        && <TeamsSettings />}
        {active === 'permissions'  && <PermissionsSettings />}
        {active === 'industries'   && <IndustriesSettings />}
        {active === 'lead_sources' && <window.LeadSourcesSettings />}
        {active === 'tags'         && <window.TagsSettings />}
        {active === 'loi_red_flags' && <><SettingsHeader title="LOI red flags" sub="Non-standard terms flagged when Atlas reads a buyer's LOI." /><window.LoiRedFlagsSettings /></>}
        {active === 'general'      && <GeneralSettings />}
        {active === 'feature_flags' && <FeatureFlagsSettings />}
        {active === 'integrations' && <IntegrationsSettings isAdmin={isAdmin} />}
        {active === 'releases'     && <ReleasesSettings />}
        {active === 'workflows'    && <PlaceholderSettings title="Workflows" />}
        {active === 'triggers'     && <PlaceholderSettings title="Triggers" />}
        {active === 'audit'        && <PlaceholderSettings title="Audit log" />}
      </div>
    </div>
  );
};

// ============ Section header ============
const SettingsHeader = ({ title, sub, action }) => (
  <div style={{ padding: '20px 28px 16px', borderBottom: '1px solid var(--border)', background: 'var(--surface)', display: 'flex', alignItems: 'flex-start', gap: 14 }}>
    <div style={{ flex: 1 }}>
      <h1 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text)', letterSpacing: '-0.015em' }}>{title}</h1>
      {sub && <div style={{ fontSize: 12.5, color: 'var(--text-3)', marginTop: 3, maxWidth: 720 }}>{sub}</div>}
    </div>
    {action}
  </div>
);

// Stub pane for settings sections that are nav-reachable but not built yet
// (Workflows, Triggers, Audit log). Renders a clear "not built" state instead
// of crashing — these were previously referenced but undefined.
const PlaceholderSettings = ({ title }) => (
  <>
    <SettingsHeader title={title} sub="This section isn't built yet." />
    <div style={{ padding: '40px 28px', color: 'var(--text-3)', fontSize: 13, maxWidth: 560 }}>
      <p style={{ margin: 0 }}>
        <strong style={{ color: 'var(--text-2)' }}>{title}</strong> is on the roadmap but not available in this build.
        Nothing to configure here yet.
      </p>
    </div>
  </>
);

// ============ INTEGRATIONS ============
// Per-user third-party connections. The catalog is server-driven
// (/api/integrations/catalog) so adding a provider or feature only requires
// editing app/integrations/catalog.py — no UI redeploy needed.

// In-page cache of the catalog so the IntegrationLogo helper (consumed by
// company_detail.jsx and other note-pill renderers) doesn't have to hit
// the API on every render. Populated by IntegrationsSettings on mount.
window.__integrationCatalogCache = window.__integrationCatalogCache || { providers: [] };

// A note's external_source can be finer-grained than a catalog provider id
// (Outlook writes 'outlook_calendar' / 'outlook_mail'). Map those to the
// provider id (for the logo) and a clean display label (for the badge).
window.__SOURCE_META = {
  fireflies:        { provider: 'fireflies', label: 'Fireflies' },
  outlook_calendar: { provider: 'microsoft', label: 'Outlook' },
  outlook_mail:     { provider: 'microsoft', label: 'Outlook' },
  microsoft:        { provider: 'microsoft', label: 'Outlook' },
};

window.IntegrationSourceLabel = (source) => {
  if (!source) return '';
  const m = window.__SOURCE_META[source];
  return m ? m.label : source.charAt(0).toUpperCase() + source.slice(1);
};

// Note-type pill label. For Outlook emails we append a direction arrow
// (↓ received / ↑ sent) read from metadata.direction so the timeline shows
// at a glance who reached out. Falls back to the plain type for everything
// else (and for emails with no direction recorded).
window.NoteTypeLabel = (note) => {
  const t = note && note.note_type;
  if (t === 'email') {
    const d = note && note.metadata && note.metadata.direction;
    if (d === 'inbound')  return 'email ↓';
    if (d === 'outbound') return 'email ↑';
  }
  return t;
};

window.IntegrationLogo = (id, size = 18) => {
  const provider = (window.__SOURCE_META[id] || {}).provider || id;
  const cat = (window.__integrationCatalogCache.providers || []).find((c) => c.id === provider);
  if (!cat || !cat.logo) return null;
  return (
    <img src={cat.logo} alt={cat.name}
      style={{ width: size, height: size, borderRadius: 3, objectFit: 'cover', flexShrink: 0 }} />
  );
};

// ── Personal email aliases ────────────────────────────────────────────────
// Addresses the user owns but that aren't @industrypro — their Gmail, iCloud,
// personal domain. Registering them tells the syncs "this is me", so meetings
// booked from a personal calendar (swim lessons, WFH blocks) stop counting as
// external meetings in Team Pulse and activity feeds. Autosaves wholesale on
// every add/remove (PUT /api/integrations/personal-emails). Reused by the
// post-OAuth prompt drawer and the Outlook card section.
const PersonalEmailsEditor = ({ autoFocus }) => {
  const [emails, setEmails] = React.useState(null);  // null = loading
  const [draft,  setDraft]  = React.useState('');
  React.useEffect(() => {
    window.AtlasApi.getPersonalEmails()
      .then((r) => setEmails(r?.emails || []))
      .catch(() => setEmails([]));
  }, []);

  const save = async (next) => {
    const prev = emails;
    setEmails(next);  // optimistic
    try {
      const r = await window.AtlasApi.setPersonalEmails(next);
      setEmails(r?.emails || next);
    } catch (e) {
      setEmails(prev);
      pushToast({ type: 'error', title: "Couldn't save personal emails", body: e.message });
    }
  };

  const add = () => {
    const v = draft.trim().toLowerCase();
    if (!v) return;
    // Same shape check the server enforces — catch typos before the round-trip.
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) {
      pushToast({ type: 'error', title: "That doesn't look like an email address", body: v });
      return;
    }
    setDraft('');
    if (!emails.includes(v)) save([...emails, v]);
  };

  if (emails === null) {
    return <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>;
  }
  return (
    <div>
      {emails.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          {emails.map((e) => (
            <span key={e} style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              fontSize: 12, padding: '3px 8px', borderRadius: 12,
              background: 'var(--surface-2)', border: '1px solid var(--border)',
              color: 'var(--text)',
            }}>
              <code className="mono" style={{ fontSize: 11.5 }}>{e}</code>
              <button onClick={() => save(emails.filter((x) => x !== e))}
                title={`Remove ${e}`}
                style={{ border: 'none', background: 'transparent', cursor: 'pointer',
                         color: 'var(--text-3)', fontSize: 12, padding: 0, lineHeight: 1 }}>
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: 6 }}>
        <TextInput value={draft} autoFocus={autoFocus}
          placeholder="you@gmail.com"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          style={{ maxWidth: 280 }} />
        <Btn size="sm" variant="outline" onClick={add} disabled={!draft.trim()}>Add</Btn>
      </div>
    </div>
  );
};

// What's-new authoring — draft a release (CalVer version + title), add items
// (kind / title / markdown body that doubles as the mini-guide / app link),
// publish. Publishing lights everyone's What's-new dot and, when the release
// has notify_slack on, DMs every linked teammate "New in Atlas: …".
const RELEASE_KIND_HUES = { feature: 'green', improvement: 'blue', fix: 'amber' };
const _todayCalVer = () => {
  const d = new Date();
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
};

const ReleasesSettings = () => {
  const [releases, setReleases] = React.useState(null);
  const [version, setVersion] = React.useState(_todayCalVer());
  const [title, setTitle] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  // Per-release new-item drafts, keyed by release id.
  const [drafts, setDrafts] = React.useState({});

  const reload = React.useCallback(() => {
    window.AtlasApi.listReleases({ include_drafts: true, limit: 30 })
      .then((r) => setReleases(Array.isArray(r) ? r : []))
      .catch(() => setReleases([]));
  }, []);
  React.useEffect(() => { reload(); }, [reload]);

  const replaceRelease = (updated) =>
    setReleases((prev) => (prev || []).map((r) => (r.id === updated.id ? updated : r)));

  const create = async () => {
    if (!version.trim() || !title.trim()) return;
    setBusy(true);
    try {
      await window.AtlasApi.createRelease({ version: version.trim(), title: title.trim() });
      setTitle('');
      reload();
    } catch (e) {
      pushToast({ type: 'error', title: 'Could not create the release', body: e.message });
    } finally {
      setBusy(false);
    }
  };

  const setDraft = (rid, patch) =>
    setDrafts((d) => ({ ...d, [rid]: { kind: 'feature', title: '', body: '', link: '', ...(d[rid] || {}), ...patch } }));

  const addItem = async (r) => {
    const d = drafts[r.id] || {};
    if (!(d.title || '').trim()) return;
    try {
      const updated = await window.AtlasApi.addReleaseItem(r.id, {
        kind: d.kind || 'feature',
        title: d.title.trim(),
        body: (d.body || '').trim() || undefined,
        link: (d.link || '').trim() || undefined,
      });
      replaceRelease(updated);
      setDrafts((prev) => ({ ...prev, [r.id]: { kind: d.kind || 'feature', title: '', body: '', link: '' } }));
    } catch (e) {
      pushToast({ type: 'error', title: 'Could not add the item', body: e.message });
    }
  };

  const removeItem = async (r, iid) => {
    try { replaceRelease(await window.AtlasApi.deleteReleaseItem(r.id, iid)); }
    catch (e) { pushToast({ type: 'error', title: 'Could not remove the item', body: e.message }); }
  };

  const publish = (r) => {
    showConfirm({
      title: `Publish ${r.version}?`,
      body: r.notify_slack
        ? 'Everyone gets the What\'s-new dot, and every Slack-linked teammate gets a "New in Atlas" DM.'
        : "Everyone gets the What's-new dot. (Slack notification is off for this release.)",
      onConfirm: async () => {
        try {
          replaceRelease(await window.AtlasApi.publishRelease(r.id));
          pushToast({ type: 'success', title: `${r.version} published` });
        } catch (e) {
          pushToast({ type: 'error', title: 'Publish failed', body: e.message });
        }
      },
    });
  };

  const removeRelease = (r) => {
    showConfirm({
      title: `Delete draft ${r.version}?`,
      body: 'The draft and its items are removed. Published releases can\'t be deleted.',
      onConfirm: async () => {
        try { await window.AtlasApi.deleteRelease(r.id); reload(); }
        catch (e) { pushToast({ type: 'error', title: 'Delete failed', body: e.message }); }
      },
    });
  };

  const inputStyle = {
    fontSize: 12, padding: '6px 10px', border: '1px solid var(--border)',
    borderRadius: 6, background: 'var(--surface)', color: 'var(--text)',
  };

  return (
    <div style={{ padding: 24, maxWidth: 800 }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
        <input value={version} onChange={(e) => setVersion(e.target.value)} placeholder="2026.08.26"
          style={{ ...inputStyle, width: 110 }} className="num" />
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Release title — e.g. Tasks 2.0 + Slack"
          onKeyDown={(e) => { if (e.key === 'Enter') create(); }}
          style={{ ...inputStyle, flex: 1 }} />
        <Btn size="sm" variant="accent" disabled={busy || !title.trim()} onClick={create}>New draft</Btn>
      </div>

      {releases === null && <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>}
      {(releases || []).map((r) => {
        const d = drafts[r.id] || { kind: 'feature', title: '', body: '', link: '' };
        const isDraft = r.status === 'draft';
        return (
          <div key={r.id} style={{
            background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10,
            padding: 16, marginBottom: 12,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="num" style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-3)' }}>{r.version}</span>
              <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text)', flex: 1 }}>{r.title}</span>
              <Pill hue={isDraft ? 'amber' : 'green'} sz="sm">{r.status}</Pill>
              {isDraft && <Btn size="xs" variant="accent" disabled={!(r.items || []).length} onClick={() => publish(r)}>Publish</Btn>}
              {isDraft && <Btn size="xs" variant="outline" onClick={() => removeRelease(r)}>Delete</Btn>}
            </div>
            {!isDraft && (
              <div style={{ fontSize: 11, color: 'var(--text-4)', marginTop: 4 }}>
                Published {r.published_at ? new Date(r.published_at).toLocaleString() : ''}
                {r.slack_notified_at ? ' · announced on Slack' : r.notify_slack ? ' · Slack announcement pending' : ' · no Slack announcement'}
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
              {(r.items || []).map((it) => (
                <div key={it.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <Pill hue={RELEASE_KIND_HUES[it.kind] || 'slate'} sz="sm">{it.kind}</Pill>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>
                      {it.title}
                      {it.link && <span style={{ fontSize: 11, color: 'var(--text-4)', marginLeft: 6 }}>{it.link}</span>}
                    </div>
                    {it.body && (
                      <div style={{ fontSize: 11.5, color: 'var(--text-3)', whiteSpace: 'pre-wrap' }}>
                        {window.renderReleaseBody ? window.renderReleaseBody(it.body) : it.body}
                      </div>
                    )}
                  </div>
                  {isDraft && (
                    <button onClick={() => removeItem(r, it.id)} title="Remove item" style={{
                      border: 'none', background: 'transparent', cursor: 'pointer',
                      color: 'var(--text-4)', fontSize: 12, padding: 0,
                    }}>✕</button>
                  )}
                </div>
              ))}
              {isDraft && (
                <div style={{ borderTop: '1px dashed var(--border)', paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <window.Picker value={d.kind} onChange={(v) => setDraft(r.id, { kind: v })} placeholder="Kind" minWidth={120}
                      options={['feature', 'improvement', 'fix']} />
                    <input value={d.title} onChange={(e) => setDraft(r.id, { title: e.target.value })}
                      placeholder="Item title — e.g. Ask Atlas from Slack" style={{ ...inputStyle, flex: 1 }} />
                    <input value={d.link} onChange={(e) => setDraft(r.id, { link: e.target.value })}
                      placeholder="#/inbox (optional)" style={{ ...inputStyle, width: 140 }} />
                  </div>
                  <textarea value={d.body} onChange={(e) => setDraft(r.id, { body: e.target.value })}
                    placeholder="Mini-guide (optional) — what it does and how to use it. **bold** and [links](#/page) render."
                    rows={2} style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
                  <div>
                    <Btn size="xs" variant="accent" disabled={!(d.title || '').trim()} onClick={() => addItem(r)}>Add item</Btn>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Slack is one workspace-level bot (the tasks daily brief), not a per-user
// credential like the catalog cards above it — so it gets its own card.
// Connect/disconnect are admin-only server-side; the card itself is visible
// to everyone so dealmakers can see the brief exists and test their DM.
// Your Slack alerts — the signed-in member's own settings (202611021500):
// what Atlas DMs them unprompted, and where approval cards go. Autosaves.
const SLACK_FEED_OPTIONS = [
  { value: 'full', label: 'Everything', hint: 'briefs, reports, alerts' },
  { value: 'action_only', label: 'Only what needs me', hint: 'decisions and hand-offs' },
];
const SLACK_APPROVALS_OPTIONS = [
  { value: 'channel', label: 'Atlas approvals channel' },
  { value: 'dm', label: 'Direct message' },
];

const SlackPrefsRow = () => {
  const [prefs, setPrefs] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => {
    window.AtlasApi.slackPrefs().then(setPrefs).catch(() => setPrefs(false));
  }, []);
  const save = async (patch) => {
    const prev = prefs;
    setPrefs({ ...prefs, ...patch });  // optimistic
    setBusy(true);
    try {
      setPrefs(await window.AtlasApi.setSlackPrefs(patch));
    } catch (e) {
      setPrefs(prev);
      pushToast({ type: 'error', title: "Couldn't save your Slack settings", body: e.message });
    } finally {
      setBusy(false);
    }
  };
  if (prefs === null) return <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 10 }}>Loading…</div>;
  if (prefs === false) return null;
  const noChannel = !prefs.approvals_channel_set;
  return (
    <div style={{ display: 'flex', gap: 16, marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)',
                  alignItems: 'center', flexWrap: 'wrap' }}>
      <span style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--text-4)',
                     textTransform: 'uppercase', letterSpacing: '0.06em' }}>Your alerts</span>
      <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: 'var(--text-2)' }}>DMs</span>
        <window.Picker value={prefs.slack_feed} disabled={busy} minWidth={190}
          title="What Atlas sends you without being asked. Approval cards always reach you."
          onChange={(v) => v && v !== prefs.slack_feed && save({ slack_feed: v })}
          options={SLACK_FEED_OPTIONS} />
      </span>
      <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: 'var(--text-2)' }}>Approvals</span>
        <window.Picker value={prefs.slack_approvals} disabled={busy} minWidth={200}
          title={noChannel
            ? 'The approvals channel isn\'t set up yet — cards come to your DMs until it is.'
            : 'Where cards that need your Approve / Deny land.'}
          onChange={(v) => v && v !== prefs.slack_approvals && save({ slack_approvals: v })}
          options={SLACK_APPROVALS_OPTIONS.map((o) => (o.value === 'channel' && noChannel
            ? { ...o, hint: 'not set up yet' } : o))} />
      </span>
      {!prefs.has_slack_id && (
        <Pill hue="amber" sz="sm" title="Ask an admin to add your Slack id in Settings → Members.">No Slack id</Pill>
      )}
    </div>
  );
};

const SlackBriefCard = ({ isAdmin = false } = {}) => {
  const [status, setStatus] = React.useState(null);
  const [token, setToken] = React.useState('');
  const [showConnect, setShowConnect] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  // Observer picker (admins): active members with a Slack id.
  const [members, setMembers] = React.useState(null);

  const reload = React.useCallback(() => {
    window.AtlasApi.slackStatus().then(setStatus).catch(() => setStatus(null));
  }, []);
  React.useEffect(() => { reload(); }, [reload]);
  React.useEffect(() => {
    if (!isAdmin) return;
    window.AtlasApi.listTeamMembers()
      .then((rows) => setMembers((rows || []).filter((m) => m.active && (m.slack_user_id || '').trim())))
      .catch(() => setMembers([]));
  }, [isAdmin]);

  const setMirror = async (email) => {
    setBusy(true);
    try {
      setStatus(await window.AtlasApi.slackMirror(email || null));
      pushToast({ type: 'success', title: email ? 'Observer set' : 'Observer cleared' });
    } catch (e) {
      pushToast({ type: 'error', title: 'Could not save the observer', body: e.message });
    } finally {
      setBusy(false);
    }
  };

  const connect = async () => {
    if (!token.trim()) return;
    setBusy(true);
    try {
      setStatus(await window.AtlasApi.slackConnect(token.trim()));
      setToken(''); setShowConnect(false);
      pushToast({ type: 'success', title: 'Slack connected', body: 'The daily task brief goes out each morning at 10am ET.' });
    } catch (e) {
      pushToast({ type: 'error', title: 'Slack connect failed', body: e.message });
    } finally {
      setBusy(false);
    }
  };
  const sendTest = async () => {
    setBusy(true);
    try {
      await window.AtlasApi.slackTest();
      pushToast({ type: 'success', title: 'Test DM sent', body: 'Check your Slack.' });
    } catch (e) {
      pushToast({ type: 'error', title: 'Test DM failed', body: e.message });
    } finally {
      setBusy(false);
    }
  };
  const disconnect = () => {
    showConfirm({
      title: 'Disconnect Slack?',
      body: 'The daily task brief stops for everyone. You can reconnect anytime.',
      onConfirm: async () => {
        try { setStatus(await window.AtlasApi.slackDisconnect()); pushToast({ type: 'success', title: 'Slack disconnected' }); }
        catch (e) { pushToast({ type: 'error', title: 'Disconnect failed', body: e.message }); }
      },
    });
  };

  const connected = !!status?.connected;
  return (
    <div style={{
      background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10,
      padding: 16, marginBottom: 12,
    }}>
      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
        <img src="assets/integrations/slack.svg" alt="Slack"
          style={{ width: 40, height: 40, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>Slack — daily task brief</div>
            {connected
              ? <Pill hue="green" sz="sm">Connected{status.workspace ? ` · ${status.workspace}` : ''}</Pill>
              : <Pill hue="slate" sz="sm">Not connected</Pill>}
          </div>
          {connected && (
            <div style={{ fontSize: 11, color: 'var(--text-4)', marginTop: 6 }}>
              {status.members_reachable} teammate(s) reachable
              {status.connected_by ? ` · connected by ${status.connected_by}` : ''}
              {status.source === 'env' ? ' · via server environment' : ''}
              {status.last_brief_date ? ` · last brief ${status.last_brief_date} (${status.last_brief_sent} sent)` : ' · no brief sent yet'}
            </div>
          )}
          {!connected && !showConnect && (
            <div style={{ fontSize: 11, color: 'var(--text-4)', marginTop: 6 }}>
              Needs a Slack app bot token with the <code>chat:write</code> scope — admins only.
            </div>
          )}
          {connected && isAdmin && (
            <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, color: 'var(--text-2)' }}>Observer copy</span>
              <window.Picker
                value={status.mirror_source === 'setting' || status.mirror_source === 'env' ? (status.mirror_to || '') : ''}
                disabled={busy || members === null}
                onChange={setMirror}
                emptyLabel="Nobody" placeholder="Observer copy" minWidth={200}
                options={[
                  ...(members || []).map((m) => ({ value: (m.email || '').toLowerCase(), label: m.full_name })),
                  ...(status.mirror_to && status.mirror_source === 'env'
                    && !(members || []).some((m) => (m.email || '').toLowerCase() === status.mirror_to)
                    ? [{ value: status.mirror_to, label: status.mirror_to_name || status.mirror_to, hint: 'server environment' }] : []),
                ]}
              />
              <span style={{ fontSize: 11, color: 'var(--text-4)' }}>
                gets a copy of every DM the bot sends — one per batch, none for DMs they already receive
                {status.mirror_source === 'env' ? ' · currently from the server environment; picking here overrides it' : ''}
              </span>
            </div>
          )}
          {connected && <SlackPrefsRow />}
          {showConnect && (
            <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                type="password"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') connect(); }}
                placeholder="xoxb-… bot token"
                autoFocus
                style={{ flex: 1, minWidth: 220, fontSize: 12, padding: '6px 10px',
                  border: '1px solid var(--border)', borderRadius: 6,
                  background: 'var(--surface)', color: 'var(--text)' }}
              />
              <Btn size="sm" variant="accent" disabled={busy || !token.trim()} onClick={connect}>Connect</Btn>
              <Btn size="sm" variant="outline" onClick={() => { setShowConnect(false); setToken(''); }}>Cancel</Btn>
            </div>
          )}
        </div>
        <div style={{ flexShrink: 0, display: 'flex', gap: 8 }}>
          {connected && <Btn size="sm" variant="outline" disabled={busy} onClick={sendTest}>Send me a test DM</Btn>}
          {connected && status.source === 'credential' && (
            <Btn size="sm" variant="outline" onClick={disconnect}>Disconnect</Btn>
          )}
          {!connected && !showConnect && (
            <Btn size="sm" variant="accent" onClick={() => setShowConnect(true)}>Connect</Btn>
          )}
        </div>
      </div>
    </div>
  );
};

const IntegrationsSettings = ({ isAdmin = false } = {}) => {
  const [catalog, setCatalog] = React.useState(null);  // null = loading
  const [conns,   setConns]   = React.useState(null);
  const [connecting, setConnecting] = React.useState(null); // {provider, step, draftFeatures}
  const [busy,    setBusy]    = React.useState(false);
  const [error,   setError]   = React.useState('');
  // Post-OAuth "add your personal emails?" prompt — optional and skippable.
  const [personalPrompt, setPersonalPrompt] = React.useState(false);

  const reload = React.useCallback(() => {
    Promise.all([
      window.AtlasApi.integrationCatalog().catch(() => ({ providers: [] })),
      window.AtlasApi.listIntegrations().catch(() => []),
    ]).then(([cat, cs]) => {
      setCatalog(cat || { providers: [] });
      setConns(cs || []);
      // Cache for IntegrationLogo helper used by note pills + elsewhere.
      window.__integrationCatalogCache = cat || { providers: [] };
    });
  }, []);
  React.useEffect(() => { reload(); }, [reload]);

  // Microsoft OAuth bounces back to "/?ms_oauth=connected&msg=email#/integrations".
  // Show the user a clear success/failure toast and strip the params from
  // the URL so a refresh doesn't re-fire the toast.
  React.useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const status = sp.get('ms_oauth');
    if (!status) return;
    const msg = sp.get('msg') || '';
    if (status === 'connected') {
      pushToast({ type: 'success', title: 'Microsoft 365 connected', body: msg });
      reload();
      // Right after connecting is the one moment users will actually register
      // their personal addresses — an env-var list nobody edited proved that.
      setPersonalPrompt(true);
    } else {
      pushToast({ type: 'error', title: 'Microsoft sign-in failed', body: msg || 'Unknown error' });
    }
    sp.delete('ms_oauth'); sp.delete('msg');
    const newQs = sp.toString();
    const newUrl = window.location.pathname + (newQs ? `?${newQs}` : '') + window.location.hash;
    window.history.replaceState({}, '', newUrl);
  }, [reload]);

  // Calendly OAuth bounces back to "/?calendly_oauth=connected&msg=email#/integrations".
  React.useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const status = sp.get('calendly_oauth');
    if (!status) return;
    const msg = sp.get('msg') || '';
    if (status === 'connected') {
      pushToast({ type: 'success', title: 'Calendly connected', body: `${msg} — now pick which event types are GPP calls in the Calendly card below.` });
      reload();
    } else {
      pushToast({ type: 'error', title: 'Calendly sign-in failed', body: msg || 'Unknown error' });
    }
    sp.delete('calendly_oauth'); sp.delete('msg');
    const newQs = sp.toString();
    window.history.replaceState({}, '', window.location.pathname + (newQs ? `?${newQs}` : '') + window.location.hash);
  }, [reload]);

  const byProvider = React.useMemo(() => {
    const m = {};
    for (const c of (conns || [])) m[c.provider] = c;
    return m;
  }, [conns]);

  const startConnect = async (provider) => {
    // OAuth2 providers (Microsoft) take a different path: fetch the
    // authorize URL with auth, then redirect the whole window. We can't 302
    // from the backend because the bearer token doesn't survive a browser
    // nav; and we can't open the drawer because there's no API key to paste.
    if (provider.auth === 'oauth2') {
      try {
        const res = await window.AtlasApi.fetch(`/api/integrations/${provider.id}/oauth/start`);
        if (res?.authorize_url) {
          window.location.href = res.authorize_url;
        } else {
          pushToast({ type: 'error', title: `Couldn't start ${provider.name} sign-in` });
        }
      } catch (e) {
        pushToast({ type: 'error', title: `${provider.name} sign-in failed`, body: e.message });
      }
      return;
    }
    // API-key flow: open the two-step drawer.
    const drafts = {};
    for (const f of (provider.features || [])) {
      drafts[f.id] = f.required || f.status === 'ready';
    }
    setConnecting({ provider, step: 'preview', draftFeatures: drafts });
    setError('');
  };

  const onSubmitConnect = async (apiKey) => {
    if (!connecting) return;
    setBusy(true); setError('');
    try {
      await window.AtlasApi.connectFireflies(apiKey, connecting.draftFeatures);
      setConnecting(null);
      reload();
      pushToast({ type: 'success', title: `Connected to ${connecting.provider.name}` });
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const onToggleFeature = async (provider, fid, enabled) => {
    const c = byProvider[provider];
    if (!c) return;
    const next = { ...(c.enabled_features || {}), [fid]: enabled };
    // Optimistic UI — flip immediately, then sync to server.
    setConns((prev) => prev.map((x) =>
      x.provider === provider ? { ...x, enabled_features: next } : x
    ));
    try {
      const updated = await window.AtlasApi.updateIntegrationFeatures(provider, next);
      setConns((prev) => prev.map((x) => x.provider === provider ? updated : x));
    } catch (e) {
      pushToast({ type: 'error', title: 'Feature toggle failed', body: e.message });
      reload();
    }
  };

  const onDisconnect = (provider) => {
    showConfirm({
      title: 'Disconnect integration?',
      body:  'This removes the stored credential and stops syncing. You can reconnect anytime.',
      onConfirm: async () => {
        try { await window.AtlasApi.disconnectIntegration(provider); reload();
          pushToast({ type: 'success', title: 'Disconnected' }); }
        catch (e) { pushToast({ type: 'error', title: 'Disconnect failed', body: e.message }); }
      },
    });
  };
  // Sync now uses an inline dropdown picker so users can choose a window.
  // Default is 90 days (recommended for a first sync — fast, verifiable);
  // power users can pick "All time" for a full historical pull.
  const [syncMenuOpenFor, setSyncMenuOpenFor] = React.useState(null);
  const [syncing, setSyncing] = React.useState(null);
  // Which provider's Activity panel is currently expanded. Null = none.
  // Single-slot rather than multi-toggle keeps the page calm — comparing
  // two integrations side-by-side would push everything else off-screen.
  const [activityFor, setActivityFor] = React.useState(null);
  // Which provider's Capabilities section is expanded. Set, not single,
  // so users can open multiple at once (lighter content than activity).
  const [expandedCaps, setExpandedCaps] = React.useState(() => new Set());
  const toggleCaps = (pid) => setExpandedCaps((prev) => {
    const next = new Set(prev);
    if (next.has(pid)) next.delete(pid); else next.add(pid);
    return next;
  });
  // Which provider's Personal-emails section is expanded (same collapsed-by-
  // default treatment as Capabilities so the card stays scannable).
  const [expandedEmails, setExpandedEmails] = React.useState(() => new Set());
  const toggleEmails = (pid) => setExpandedEmails((prev) => {
    const next = new Set(prev);
    if (next.has(pid)) next.delete(pid); else next.add(pid);
    return next;
  });

  const onSync = async (provider, days) => {
    setSyncing(provider);
    setSyncMenuOpenFor(null);
    try {
      await window.AtlasApi.triggerIntegrationSync(provider, days);
      pushToast({ type: 'success', title: 'Sync started',
                  body: `Pulling last ${days >= 3000 ? 'all' : days + ' days'} of meetings. Notes will appear as they land.` });
      // Auto-open the activity panel — most likely thing the user wants to
      // do next is watch the meetings show up. Reloads on a delay so the
      // first batch has time to land.
      setActivityFor(provider);
      setTimeout(reload, 5000);
    } catch (e) {
      pushToast({ type: 'error', title: 'Sync failed', body: e.message });
    } finally {
      setSyncing(null);
    }
  };

  // Deep-history backfill — queues a per-credential job the reconcile worker
  // drains one per hourly tick (no more global env-knob + redeploy ritual).
  const [backfilling, setBackfilling] = React.useState(null);
  const onBackfill = async (cred) => {
    setBackfilling(cred.id);
    try {
      const res = await window.AtlasApi.requestIntegrationBackfill(cred.id);
      pushToast({ type: 'success', title: 'Full-history backfill queued',
                  body: `Will pull history back to ${res.backfill_since}. Runs with the next hourly sync.` });
      reload();
    } catch (e) {
      pushToast({ type: 'error', title: 'Backfill request failed', body: e.message });
    } finally {
      setBackfilling(null);
    }
  };

  const SYNC_OPTIONS = [
    { days: 7,    label: 'Last 7 days' },
    { days: 30,   label: 'Last 30 days' },
    { days: 90,   label: 'Last 90 days' },
    { days: 365,  label: 'Last year' },
    { days: 3650, label: 'All time' },
  ];

  const fmtAgo = (iso) => {
    if (!iso) return 'never';
    const d = new Date(iso);
    const sec = (Date.now() - d.getTime()) / 1000;
    if (sec < 60)   return 'just now';
    if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
    if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
    return d.toLocaleDateString();
  };

  const providers = catalog?.providers || [];

  return (
    <div style={{ padding: 24, maxWidth: 800 }}>
      {/* No inner heading. Both call sites (Settings → Integrations pane and
          the top-level IntegrationsView with its PageHeader) already announce
          the section — a second h2 just repeated the title on the page. */}

      {/* DealCloud — the system-of-record source. Not a per-user OAuth
          connection: it's a one-way migration/sync managed by the admin, so
          there's nothing to "connect" here. Shown so users understand where
          Atlas's records come from rather than wondering why it's absent. */}
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10,
        padding: 16, marginBottom: 12,
      }}>
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
          <div style={{ width: 40, height: 40, borderRadius: 6, background: 'var(--surface-2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            fontSize: 13, fontWeight: 700, color: 'var(--text-3)', letterSpacing: '-0.02em' }}>
            DC
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>DealCloud</div>
              <Pill hue="slate" sz="sm">System source</Pill>
            </div>
          </div>
          <div style={{ flexShrink: 0 }}>
            <Btn size="sm" variant="outline" disabled title="Managed by your workspace admin">Managed by admin</Btn>
          </div>
        </div>
      </div>

      {catalog === null && <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>}
      {catalog && providers.map((cat) => {
        const c = byProvider[cat.id];
        const enabledCount = c ? Object.values(c.enabled_features || {}).filter(Boolean).length : 0;
        const readyCount   = (cat.features || []).filter((f) => f.status === 'ready').length;
        return (
          <div key={cat.id} style={{
            background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10,
            padding: 16, marginBottom: 12,
          }}>
            <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
              {cat.logo ? (
                <img src={cat.logo} alt={cat.name}
                  style={{ width: 40, height: 40, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }} />
              ) : (
                <div style={{ width: 40, height: 40, borderRadius: 6, background: 'var(--surface-2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
                  ⚡
                </div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{cat.name}</div>
                  {c && c.active && <Pill hue="green" sz="sm">Connected</Pill>}
                  {c && !c.active && <Pill hue="slate" sz="sm">Paused</Pill>}
                </div>
                {c ? (
                  <>
                    <div style={{ fontSize: 11, color: 'var(--text-4)', marginTop: 6 }}>
                      Connected as <code className="mono">{c.account_email || c.account_name || '(unknown)'}</code>
                      {' · '}last synced {fmtAgo(c.last_synced_at)}
                      {' · '}{enabledCount} of {readyCount} ready features on
                    </div>
                    {/* Live sync status: in-flight, error, or last summary.
                        Polls the credential row every 4s while a sync is
                        running (last_sync_started_at > last_synced_at). */}
                    <SyncStatusLine cred={c} onTick={reload} />
                    {/* The webhook is a second, independent clock and the card
                        only ever showed the sweep's. */}
                    <WebhookStatusLine cred={c} />
                    {/* Deep-history backfill state (queued/running/done/error)
                        + a truncation nudge when the last sync hit its cap. */}
                    <BackfillStatusLine cred={c} />
                  </>
                ) : (
                  <div style={{ fontSize: 11, color: 'var(--text-4)', marginTop: 6 }}>
                    {(cat.features || []).filter((f) => f.status === 'ready').length} ready capabilities
                    {' · '}
                    {(cat.features || []).filter((f) => f.status === 'coming_soon').length} more coming soon
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0, position: 'relative' }}>
                {c ? (
                  <>
                    <Btn size="sm" variant="accent"
                      onClick={() => setSyncMenuOpenFor(syncMenuOpenFor === cat.id ? null : cat.id)}
                      disabled={syncing === cat.id}>
                      {syncing === cat.id ? 'Syncing…' : 'Sync now ▾'}
                    </Btn>
                    {syncMenuOpenFor === cat.id && (
                      <div style={{
                        position: 'absolute', top: 32, right: 0, zIndex: 60,
                        background: 'var(--surface)', border: '1px solid var(--border)',
                        borderRadius: 8, boxShadow: 'var(--shadow-lg)',
                        minWidth: 240, padding: 4,
                      }}>
                        {SYNC_OPTIONS.map((opt) => (
                          <button key={opt.days}
                            onClick={() => onSync(cat.id, opt.days)}
                            style={{
                              display: 'block', width: '100%', textAlign: 'left',
                              padding: '7px 10px', border: 'none', background: 'transparent',
                              cursor: 'pointer', borderRadius: 5, fontSize: 12.5,
                              color: 'var(--text)', fontWeight: 500,
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-hover)'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    )}
                    <Btn size="sm" variant={activityFor === cat.id ? 'accent' : 'outline'}
                      onClick={() => setActivityFor(activityFor === cat.id ? null : cat.id)}>
                      {activityFor === cat.id ? 'Hide activity' : 'View activity'}
                    </Btn>
                    <Btn size="sm" variant="outline"
                      onClick={() => onBackfill(c)}
                      disabled={c.backfill_status === 'queued' || c.backfill_status === 'running' || backfilling === c.id}
                      title="Queue a deep-history sync (default: last year) — runs with the next hourly sync">
                      {c.backfill_status === 'queued' ? 'Backfill queued'
                        : c.backfill_status === 'running' ? 'Backfilling…'
                        : 'Backfill full history'}
                    </Btn>
                    <Btn size="sm" variant="outline" onClick={() => onDisconnect(cat.id)}>Disconnect</Btn>
                  </>
                ) : (
                  <Btn size="sm" variant="accent" onClick={() => startConnect(cat)}>Connect</Btn>
                )}
              </div>
            </div>

            {/* Feature list — visible inline on connected integrations.
                Disconnected ones get the same list in the connect drawer.
                Collapsed by default so the page stays scannable when more
                integrations are added; one click reveals the toggles. */}
            {c && (() => {
              const capsOpen = expandedCaps.has(cat.id);
              const flags = c.enabled_features || {};
              const readyOn = (cat.features || [])
                .filter((f) => f.status === 'ready' && flags[f.id])
                .map((f) => f.label);
              return (
                <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
                  <button onClick={() => toggleCaps(cat.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6, width: '100%',
                      background: 'transparent', border: 'none', cursor: 'pointer',
                      padding: 0, textAlign: 'left',
                    }}>
                    <span style={{ fontSize: 10.5, color: 'var(--text-4)' }}>
                      {capsOpen ? '▾' : '▸'}
                    </span>
                    <span style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--text-4)',
                                   textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      Capabilities
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--text-3)', marginLeft: 4 }}>
                      ({readyOn.length} of {(cat.features || []).filter((f) => f.status === 'ready').length} enabled
                      {!capsOpen && readyOn.length > 0 ? ` · ${readyOn.slice(0, 3).join(', ')}${readyOn.length > 3 ? '…' : ''}` : ''})
                    </span>
                  </button>
                  {capsOpen && (
                    <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {(cat.features || []).map((f) => (
                        <FeatureRow key={f.id} feature={f}
                          enabled={Boolean(flags[f.id])}
                          onToggle={(v) => onToggleFeature(cat.id, f.id, v)} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Personal emails — Outlook only (that's where the "my personal
                address looks external" problem bites: calendar + mail drive
                Team Pulse's external-meeting counts). Existing notes keep the
                attendee lists stamped at ingest — hence the re-sync hint. */}
            {c && cat.id === 'microsoft' && (() => {
              const open = expandedEmails.has(cat.id);
              return (
                <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
                  <button onClick={() => toggleEmails(cat.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6, width: '100%',
                      background: 'transparent', border: 'none', cursor: 'pointer',
                      padding: 0, textAlign: 'left',
                    }}>
                    <span style={{ fontSize: 10.5, color: 'var(--text-4)' }}>
                      {open ? '▾' : '▸'}
                    </span>
                    <span style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--text-4)',
                                   textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      Personal emails
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--text-3)', marginLeft: 4 }}>
                      (so your own Gmail/iCloud doesn't count as an external contact)
                    </span>
                  </button>
                  {open && (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontSize: 11.5, color: 'var(--text-3)', lineHeight: 1.5, marginBottom: 8 }}>
                        Changes apply to future syncs — run "Sync now" to re-classify what's already imported.
                      </div>
                      <PersonalEmailsEditor />
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Activity panel — appears inside the card only when the user
                clicks "View activity". Per-provider so each integration's
                history stays scoped to its own card. */}
            {c && activityFor === cat.id && (
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
                <IntegrationActivityPanel provider={cat.id} />
              </div>
            )}
          </div>
        );
      })}

      <SlackBriefCard isAdmin={isAdmin} />

      {/* Post-OAuth prompt: optional, skippable. The editor autosaves, so
          both the Skip button and the drawer's own close just dismiss. */}
      {personalPrompt && (
        <Drawer open={true} onClose={() => setPersonalPrompt(false)}
          title="Add your personal email addresses?"
          subtitle="Optional — you can skip this and edit the list anytime on the Outlook card."
          footer={(
            <>
              <Btn size="sm" variant="outline" onClick={() => setPersonalPrompt(false)}>Skip</Btn>
              <Btn size="sm" variant="accent" onClick={() => setPersonalPrompt(false)}>Done</Btn>
            </>
          )}>
          <div style={{ fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.6, marginBottom: 14 }}>
            If you ever book meetings or send mail from a personal address (Gmail, iCloud,
            a family calendar…), add it here so Atlas knows those are you. Otherwise personal
            calendar events can be counted as external meetings in Team Pulse and activity feeds.
          </div>
          <PersonalEmailsEditor autoFocus />
        </Drawer>
      )}

      {/* Two-step Connect drawer */}
      {connecting && connecting.step === 'preview' && (
        <Drawer open={true} onClose={() => !busy && setConnecting(null)}
          title={`Connect ${connecting.provider.name}`}
          subtitle={`Here's what you'll get. Toggle any you don't want, then continue to enter the API key.`}
          footer={(
            <>
              <Btn size="sm" variant="outline" onClick={() => setConnecting(null)} disabled={busy}>Cancel</Btn>
              <Btn size="sm" variant="accent"
                onClick={() => setConnecting({ ...connecting, step: 'key' })}>
                Continue →
              </Btn>
            </>
          )}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {(connecting.provider.features || []).map((f) => (
              <FeatureRow key={f.id} feature={f}
                enabled={Boolean(connecting.draftFeatures[f.id])}
                onToggle={(v) => setConnecting((prev) => ({
                  ...prev,
                  draftFeatures: { ...prev.draftFeatures, [f.id]: v }
                }))} />
            ))}
          </div>
        </Drawer>
      )}

      {connecting && connecting.step === 'key' && (
        <Drawer open={true} onClose={() => !busy && setConnecting(null)}
          title={`Connect ${connecting.provider.name}`}
          subtitle={connecting.provider.key_help}
          footer={(
            <>
              <Btn size="sm" variant="outline"
                onClick={() => setConnecting({ ...connecting, step: 'preview' })}
                disabled={busy}>← Back</Btn>
              <Btn size="sm" variant="accent" onClick={() => {
                const v = document.getElementById('integration-api-key').value;
                onSubmitConnect(v.trim());
              }} disabled={busy}>
                {busy ? 'Verifying…' : 'Connect'}
              </Btn>
            </>
          )}>
          <Field label="API key"
            hint={<>From <a href={connecting.provider.docs_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-text)' }}>{connecting.provider.docs_url}</a></>}>
            <input id="integration-api-key" type="password"
              placeholder={connecting.provider.placeholder}
              style={{ width: '100%', padding: '6px 10px', fontSize: 12.5,
                       border: '1px solid var(--border)', borderRadius: 5,
                       fontFamily: 'var(--font-mono)' }} />
          </Field>
          {error && (
            <div style={{ marginTop: 8, fontSize: 11.5, color: 'var(--rose-fg)' }}>{error}</div>
          )}
          <div style={{ marginTop: 12, fontSize: 11, color: 'var(--text-4)', lineHeight: 1.5 }}>
            The key is verified against {connecting.provider.name} before saving.
            On success it's encrypted with <code className="mono">ATLAS_SECRET_KEY</code> and stored
            in your private integration_credentials row — not visible to other workspace members.
          </div>
        </Drawer>
      )}
      <CalendlyGppCard isAdmin={isAdmin} connected={!!byProvider.calendly} />
      {isAdmin && <window.WebsiteTrackingCard />}
      {isAdmin && <OrgMailboxAccess />}
      {isAdmin && <FirecrawlSignalsCard />}
    </div>
  );
};

// Firecrawl monitors ("Signals", admin only). Firecrawl re-reads pages and
// re-runs web searches on a schedule, judges whether a change matters, and
// POSTs to /api/integrations/firecrawl/webhook; this card shows whether that
// loop is wired (key, secret, public URL), the monitors in the registry, and
// what has arrived. Pipelines register monitors themselves (buyer sites,
// industry signals, ...) — this is the operator's view, not the creator.
const FirecrawlSignalsCard = () => {
  const [status, setStatus] = React.useState(null);      // null = loading
  const [monitors, setMonitors] = React.useState([]);
  const [events, setEvents] = React.useState(null);      // null = collapsed
  const [busy, setBusy] = React.useState(null);          // 'sync' | 'process' | monitor_id
  const [error, setError] = React.useState(null);

  const reload = React.useCallback(() => {
    Promise.all([window.AtlasApi.firecrawlStatus(), window.AtlasApi.firecrawlMonitors()])
      .then(([s, m]) => { setStatus(s); setMonitors(m.items || []); setError(null); })
      .catch((e) => { setStatus({}); setError(e.message); });
  }, []);
  React.useEffect(() => { reload(); }, [reload]);

  const loadEvents = React.useCallback(() => {
    window.AtlasApi.firecrawlEvents({ limit: 50 })
      .then((r) => setEvents(r.items || []))
      .catch((e) => pushToast({ type: 'error', title: 'Could not load events', body: e.message }));
  }, []);

  const run = async (key, fn, okTitle) => {
    setBusy(key);
    try {
      const r = await fn();
      if (okTitle) pushToast({ type: 'success', title: okTitle, body: r && typeof r === 'object' && !r.items
        ? Object.entries(r).filter(([, v]) => typeof v !== 'object').map(([k, v]) => `${k}: ${v}`).join(' · ')
        : undefined });
      reload();
      if (events) loadEvents();
    } catch (e) {
      pushToast({ type: 'error', title: 'Firecrawl request failed', body: e.message });
    } finally { setBusy(null); }
  };

  const remove = (m) => {
    showConfirm({
      title: `Delete monitor "${m.name}"?`,
      body: 'Firecrawl stops checking and drops its check history. Events already received stay in Atlas.',
      danger: true,
      onConfirm: () => run(m.monitor_id, () => window.AtlasApi.firecrawlDeleteMonitor(m.monitor_id), 'Monitor deleted'),
    });
  };

  const wired = !!(status && status.api_key_configured && status.webhook_secret_configured && status.webhook_url);
  const ev = (status && status.events) || {};
  const fmtWhen = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso); const mins = Math.round((Date.now() - d.getTime()) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    if (mins < 60 * 24) return `${Math.round(mins / 60)}h ago`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };
  const kindHue = { buyer_site: 'blue', industry_signal: 'amber', company_watch: 'violet', generic: 'slate' };
  const statusHue = { active: 'green', paused: 'amber', deleted: 'slate' };
  const evHue = { changed: 'amber', new: 'green', removed: 'rose', error: 'rose', same: 'slate', completed: 'slate' };
  const cell = { padding: '6px 8px', fontSize: 12, borderBottom: '1px solid var(--border)', verticalAlign: 'top' };
  const head = { ...cell, fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--text-4)', fontWeight: 600 };
  const chip = (ok, label) => (
    <Pill hue={ok ? 'green' : 'rose'} sz="sm">{label}{ok ? '' : ' missing'}</Pill>
  );

  return (
    <div style={{
      background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10,
      padding: 16, marginBottom: 12,
    }}>
      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
        <div style={{ width: 40, height: 40, borderRadius: 6, flexShrink: 0, display: 'grid', placeItems: 'center',
          background: 'var(--amber-bg)', color: 'var(--amber-fg)', fontSize: 20 }} aria-hidden="true">🔥</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>Firecrawl — Signals</div>
            {status === null ? <Pill hue="slate" sz="sm">Loading…</Pill>
              : wired ? <Pill hue="green" sz="sm">Wired</Pill>
              : <Pill hue="amber" sz="sm">Needs setup</Pill>}
          </div>
          {error && <div style={{ fontSize: 11.5, color: 'var(--rose-fg)', marginTop: 6 }}>{error}</div>}
          {status && !error && (
            <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              {chip(status.api_key_configured, 'API key')}
              {chip(status.webhook_secret_configured, 'Webhook secret')}
              {status.webhook_url
                ? <Pill hue="green" sz="sm" maxW={360} title={status.webhook_url}>Webhook · {status.webhook_url}</Pill>
                : <Pill hue="rose" sz="sm">Webhook URL missing (FIRECRAWL_WEBHOOK_URL)</Pill>}
              <span style={{ fontSize: 11, color: 'var(--text-4)' }}>
                {(status.monitors && status.monitors.active) || 0} active monitor(s) · {ev.last_24h || 0} event(s) last 24h
                {ev.pending ? ` · ${ev.pending} pending` : ''}{ev.errors ? ` · ${ev.errors} error(s)` : ''}
                {ev.last_at ? ` · last ${fmtWhen(ev.last_at)}` : ' · nothing received yet'}
                {status.usd_per_credit ? ` · $${status.usd_per_credit}/credit` : ''}
              </span>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            <Btn size="sm" variant="outline" disabled={busy === 'sync'}
              onClick={() => run('sync', window.AtlasApi.firecrawlSyncMonitors, 'Registry synced')}>
              {busy === 'sync' ? 'Syncing…' : 'Sync from Firecrawl'}
            </Btn>
            <Btn size="sm" variant="outline" disabled={busy === 'process' || !ev.pending}
              title={ev.pending ? 'Run handlers over unprocessed deliveries' : 'Nothing pending'}
              onClick={() => run('process', window.AtlasApi.firecrawlProcessEvents, 'Pending events processed')}>
              Process pending
            </Btn>
            <Btn size="sm" variant="ghost" onClick={() => (events ? setEvents(null) : loadEvents())}>
              {events ? 'Hide deliveries' : 'Show recent deliveries'}
            </Btn>
          </div>

          {monitors.length > 0 && (
            <div style={{ marginTop: 12, overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ ...head, textAlign: 'left' }}>Monitor</th>
                    <th style={{ ...head, textAlign: 'left' }}>Kind</th>
                    <th style={{ ...head, textAlign: 'left' }}>Schedule</th>
                    <th style={{ ...head, textAlign: 'left' }}>Status</th>
                    <th style={{ ...head, textAlign: 'left' }}>Last check</th>
                    <th style={{ ...head, textAlign: 'right' }}>Events 30d</th>
                    <th style={{ ...head, textAlign: 'right' }}>Credits/mo</th>
                    <th style={head} />
                  </tr>
                </thead>
                <tbody>
                  {monitors.map((m) => (
                    <tr key={m.monitor_id}>
                      <td style={cell}>
                        <div style={{ fontWeight: 500, color: 'var(--text)' }}>{m.name}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-4)' }}>
                          {m.target_type}{m.goal ? ` · ${m.goal}` : ''}
                        </div>
                      </td>
                      <td style={cell}><Pill hue={kindHue[m.kind] || 'slate'} sz="sm">{m.kind.replace('_', ' ')}</Pill></td>
                      <td style={{ ...cell, whiteSpace: 'nowrap' }}>{m.schedule_text || '—'}</td>
                      <td style={cell}><Pill hue={statusHue[m.status] || 'slate'} sz="sm">{m.status}</Pill></td>
                      <td style={{ ...cell, whiteSpace: 'nowrap' }} title={m.last_check_summary ? JSON.stringify(m.last_check_summary) : ''}>
                        {fmtWhen(m.last_check_at)}
                      </td>
                      <td style={{ ...cell, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {m.events && m.events.total ? `${m.events.total} · ${m.events.meaningful || 0} meaningful` : '—'}
                        {m.events && m.events.errors ? <span style={{ color: 'var(--rose-fg)' }}> · {m.events.errors} err</span> : null}
                      </td>
                      <td style={{ ...cell, textAlign: 'right' }}>
                        {m.estimated_credits_per_month != null ? Math.round(m.estimated_credits_per_month).toLocaleString() : '—'}
                      </td>
                      <td style={{ ...cell, whiteSpace: 'nowrap', textAlign: 'right' }}>
                        {m.status === 'active' && (
                          <Btn size="sm" variant="ghost" disabled={busy === m.monitor_id}
                            onClick={() => run(m.monitor_id, () => window.AtlasApi.firecrawlMonitorAction(m.monitor_id, 'pause'), 'Monitor paused')}>Pause</Btn>
                        )}
                        {m.status === 'paused' && (
                          <Btn size="sm" variant="ghost" disabled={busy === m.monitor_id}
                            onClick={() => run(m.monitor_id, () => window.AtlasApi.firecrawlMonitorAction(m.monitor_id, 'resume'), 'Monitor resumed')}>Resume</Btn>
                        )}
                        <Btn size="sm" variant="ghost" disabled={busy === m.monitor_id} onClick={() => remove(m)}>Delete</Btn>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {status && !error && monitors.length === 0 && (
            <div style={{ fontSize: 11.5, color: 'var(--text-4)', marginTop: 10 }}>
              No monitors in the registry yet. Pipelines create theirs; a monitor made in the Firecrawl
              dashboard appears after “Sync from Firecrawl”.
            </div>
          )}

          {events && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 11, color: 'var(--text-4)', marginBottom: 4 }}>
                Latest {events.length} deliveries
              </div>
              {events.length === 0 && (
                <div style={{ fontSize: 11.5, color: 'var(--text-4)' }}>Nothing has arrived yet.</div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {events.map((e) => (
                  <div key={e.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12,
                    padding: '5px 8px', border: '1px solid var(--border)', borderRadius: 6 }}>
                    <span style={{ color: 'var(--text-4)', whiteSpace: 'nowrap', minWidth: 60 }}>{fmtWhen(e.received_at)}</span>
                    <Pill hue={evHue[e.status] || 'slate'} sz="sm">{e.status || e.event_type}</Pill>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <span style={{ color: 'var(--text)' }}>{e.monitor_name || e.monitor_id}</span>
                        {e.url ? <> · <a href={e.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-text)' }}>{e.url}</a></> : null}
                      </div>
                      {e.reason && <div style={{ color: 'var(--text-3)' }}>{e.reason}</div>}
                      {e.summary && !e.url && (
                        <div style={{ color: 'var(--text-4)', fontSize: 11 }}>
                          {Object.entries(e.summary).map(([k, v]) => `${k} ${v}`).join(' · ')}
                        </div>
                      )}
                      {e.error && <div style={{ color: 'var(--rose-fg)', fontSize: 11 }}>{e.error}</div>}
                    </div>
                    {e.is_meaningful ? <Pill hue="green" sz="sm">meaningful</Pill> : null}
                    {!e.processed_at ? <Pill hue="amber" sz="sm">pending</Pill> : null}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Organization-granted mailbox access (admin only). Instead of every person
// clicking Connect, the org grants the Atlas app Mail.Read + Calendars.Read as
// Application permissions once; this block shows whether that grant is in
// place and opens individual mailboxes — departed members' included —
// without anyone signing in. Backed by /api/integrations/microsoft/org-access.
// Calendly → Growth Partner Program intake. Connecting is the generic
// Calendly card above (OAuth); this card is the program side of it: which
// event types are GPP calls, who runs the program (default owner of every
// new partner + the portal's sending mailbox), the Slack channel, the
// webhook, and an "import recent" button for calls that already happened.
const CalendlyGppCard = ({ isAdmin, connected }) => {
  const [status, setStatus] = React.useState(null);
  const [types, setTypes] = React.useState(null);
  const [members, setMembers] = React.useState([]);
  const [channels, setChannels] = React.useState(null);   // null = loading, {error} = fall back to the id box
  const [draft, setDraft] = React.useState(null);
  const [busy, setBusy] = React.useState(null);

  const reload = React.useCallback(() => {
    window.AtlasApi.calendlyStatus().then((s) => {
      setStatus(s);
      setDraft({ event_type_uris: s.settings.event_type_uris || [], slack_channel: s.settings.slack_channel || '',
                 director_member_id: s.director ? s.director.member_id : '', min_start_days_back: s.settings.min_start_days_back || 30 });
    }).catch((e) => setStatus({ configured: false, config_error: e.message, accounts: [], settings: {}, director: null }));
  }, []);
  React.useEffect(() => { reload(); }, [reload, connected]);
  React.useEffect(() => {
    if (!status || !status.accounts.length) { setTypes(null); return; }
    window.AtlasApi.calendlyEventTypes().then((r) => setTypes(r.event_types || [])).catch(() => setTypes([]));
  }, [status]);
  React.useEffect(() => {
    if (!isAdmin) return;
    window.AtlasApi.listTeamMembers().then((r) => setMembers((Array.isArray(r) ? r : (r.items || r.members || [])).filter((m) => m.active !== false))).catch(() => {});
    window.AtlasApi.calendlySlackChannels().then((r) => setChannels(r.error ? { error: r.error, list: [] } : { list: r.channels || [] }))
      .catch((e) => setChannels({ error: e.message, list: [] }));
  }, [isAdmin]);

  const save = async () => {
    setBusy('save');
    try {
      const s = await window.AtlasApi.calendlySaveSettings(draft);
      setStatus(s);
      window.pushToast({ type: 'success', title: 'Calendly settings saved' });
    } catch (e) { window.pushToast({ type: 'error', title: 'Could not save', body: e.message }); }
    finally { setBusy(null); }
  };
  const importRecent = async () => {
    setBusy('import');
    try {
      const r = await window.AtlasApi.calendlyImportRecent();
      const lines = (r.results || []).map((x) => x.error ? `${x.account_email}: ${x.error}`
        : `${x.account_email}: ${x.events} event(s), ${x.invitees} invitee(s) — ${Object.entries(x.actions || {}).map(([k, v]) => `${k} ${v}`).join(', ') || 'nothing new'}`);
      window.pushToast({ type: 'success', title: 'Calendly import finished', body: lines.join('\n') });
      reload();
    } catch (e) { window.pushToast({ type: 'error', title: 'Import failed', body: e.message }); }
    finally { setBusy(null); }
  };
  const registerWebhook = async () => {
    setBusy('webhook');
    try {
      const r = await window.AtlasApi.calendlyRegisterWebhook();
      const bad = (r.results || []).filter((x) => !x.ok);
      window.pushToast({ type: bad.length ? 'error' : 'success', title: bad.length ? 'Webhook not registered' : 'Webhook registered',
                         body: (r.results || []).map((x) => `${x.account_email}: ${x.detail}`).join('\n') });
      reload();
    } catch (e) { window.pushToast({ type: 'error', title: 'Webhook registration failed', body: e.message }); }
    finally { setBusy(null); }
  };
  const toggleType = (uri) => setDraft((d) => ({ ...d, event_type_uris: d.event_type_uris.includes(uri) ? d.event_type_uris.filter((u) => u !== uri) : [...d.event_type_uris, uri] }));

  if (status === null) return null;
  const accounts = status.accounts || [];
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginTop: 20 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13.5, fontWeight: 600 }}>Calendly → Growth Partner Program</div>
        <div style={{ flex: 1 }} />
        {accounts.length > 0 && <Btn size="sm" variant="outline" disabled={busy !== null} onClick={importRecent}>{busy === 'import' ? 'Importing…' : `Import the last ${draft ? draft.min_start_days_back : 30} days`}</Btn>}
        {accounts.length > 0 && isAdmin && <Btn size="sm" variant="outline" disabled={busy !== null} onClick={registerWebhook}>{busy === 'webhook' ? 'Registering…' : 'Register webhook'}</Btn>}
      </div>
      {!status.configured && <div style={{ marginTop: 8 }}><Pill hue="rose" sz="sm">{status.config_error || 'Calendly app not configured on the server'}</Pill></div>}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 10 }}>
        {accounts.length === 0 && <Pill hue="slate" sz="sm">No Calendly account connected yet</Pill>}
        {accounts.map((a) => (
          <Pill key={a.credential_id} hue={a.last_sync_error ? 'rose' : 'green'} dot sz="sm">
            {a.account_email}{a.webhook ? ' · webhook on' : ' · polling'}{a.last_sync_error ? ' · ' + a.last_sync_error.slice(0, 60) : ''}
          </Pill>
        ))}
        {status.director && <Pill hue="teal" sz="sm">Director: {status.director.name}</Pill>}
      </div>
      {draft && accounts.length > 0 && (
        <div style={{ marginTop: 12, display: 'grid', gap: 12, gridTemplateColumns: 'minmax(260px, 1fr) minmax(260px, 1fr)' }}>
          <div>
            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-4)', marginBottom: 6 }}>Event types that are GPP calls</div>
            {types === null && <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Loading event types…</div>}
            {types && types.length === 0 && <div style={{ fontSize: 12, color: 'var(--text-3)' }}>No event types found.</div>}
            {types && types.filter((t) => t.uri).map((t) => (
              <label key={t.uri} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5, padding: '3px 0', cursor: isAdmin ? 'pointer' : 'default' }}>
                <input type="checkbox" disabled={!isAdmin} checked={draft.event_type_uris.includes(t.uri)} onChange={() => toggleType(t.uri)} />
                <span>{t.name}</span><span style={{ color: 'var(--text-4)', fontSize: 11 }}>{t.account_email}</span>
              </label>
            ))}
            <div style={{ fontSize: 11, color: 'var(--text-4)', marginTop: 4 }}>Nothing checked = every event type of the connected account counts.</div>
          </div>
          <div style={{ display: 'grid', gap: 10, alignContent: 'start' }}>
            <label style={{ fontSize: 12.5 }}>
              <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-4)', marginBottom: 4 }}>GPP director (owns every new partner)</div>
              <window.Picker disabled={!isAdmin} value={draft.director_member_id || ''} onChange={(v) => setDraft((d) => ({ ...d, director_member_id: v }))}
                width="100%" placeholder="GPP director" emptyLabel={`— default (${status.director ? status.director.name : 'first admin'}) —`}
                options={members.map((m) => ({ value: String(m.id), label: m.full_name, hint: m.email || undefined }))} />
            </label>
            <label style={{ fontSize: 12.5 }}>
              <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-4)', marginBottom: 4 }}>Slack channel for booking alerts (optional)</div>
              {channels && channels.list.length > 0 ? (
                <window.Picker disabled={!isAdmin} value={draft.slack_channel || ''} onChange={(v) => setDraft((d) => ({ ...d, slack_channel: v }))}
                  width="100%" placeholder="Slack channel" emptyLabel="— no Slack alert —"
                  options={channels.list.map((c) => ({ value: c.id, label: `#${c.name}`, hint: [c.is_private ? 'private' : null, c.is_member ? null : 'bot not in channel'].filter(Boolean).join(' · ') }))} />
              ) : (
                <input disabled={!isAdmin} value={draft.slack_channel} onChange={(e) => setDraft((d) => ({ ...d, slack_channel: e.target.value }))} placeholder="Channel id, e.g. C0123ABCD"
                  style={{ width: '100%', padding: '6px 8px', fontSize: 12.5, border: '1px solid var(--border)', borderRadius: 5, fontFamily: 'var(--font-mono)', background: 'var(--surface)', color: 'var(--text)' }} />
              )}
              {channels && channels.error && <div style={{ fontSize: 11, color: 'var(--text-4)', marginTop: 3 }}>{channels.error}</div>}
              {channels && channels.list.length > 0 && <div style={{ fontSize: 11, color: 'var(--text-4)', marginTop: 3 }}>Invite the Atlas bot to a channel before picking it, or the alert has nowhere to land.</div>}
            </label>
            {isAdmin && <div><Btn size="sm" variant="accent" disabled={busy !== null} onClick={save}>{busy === 'save' ? 'Saving…' : 'Save'}</Btn></div>}
            {status.webhook_url && <div style={{ fontSize: 11, color: 'var(--text-4)', fontFamily: 'var(--font-mono)', wordBreak: 'break-all' }}>webhook: {status.webhook_url}</div>}
          </div>
        </div>
      )}
    </div>
  );
};

const OrgMailboxAccess = () => {
  const [status, setStatus] = React.useState(null);      // null = loading
  const [busy, setBusy] = React.useState(null);          // member_id | 'all'
  const [showInactive, setShowInactive] = React.useState(true);

  const reload = React.useCallback(() => {
    window.AtlasApi.orgMailboxStatus()
      .then(setStatus)
      .catch((e) => setStatus({ configured: false, roles: [], missing: [], mailboxes: [], error: e.message }));
  }, []);
  React.useEffect(() => { reload(); }, [reload]);

  const granted = !!(status && status.configured && status.missing.length === 0);

  const connect = async (m) => {
    setBusy(m.member_id);
    try {
      await window.AtlasApi.orgMailboxConnect(m.member_id);
      window.pushToast({ type: 'success', title: `${m.email} connected`, body: 'Syncing through the organization grant; history pull is queued.' });
      reload();
    } catch (e) {
      window.pushToast({ type: 'error', title: `Could not connect ${m.email}`, body: e.message });
    } finally { setBusy(null); }
  };

  const connectAll = async () => {
    setBusy('all');
    try {
      const r = await window.AtlasApi.orgMailboxConnectAll(showInactive);
      window.pushToast({
        type: r.failed.length ? 'error' : 'success',
        title: `${r.connected.length} connected · ${r.skipped.length} already connected · ${r.failed.length} failed`,
        body: r.failed.map((f) => `${f.email}: ${f.detail}`).join('\n') || undefined,
      });
      reload();
    } catch (e) {
      window.pushToast({ type: 'error', title: 'Connect everyone failed', body: e.message });
    } finally { setBusy(null); }
  };

  const rows = (status?.mailboxes || []).filter((m) => showInactive || m.member_active);
  const modeLabel = { delegated: 'signed in', application: 'organization', none: 'not connected' };
  const rowHue = (m) => {
    if (m.mode === 'none') return 'slate';
    if (m.last_sync_error || m.credential_active === false) return 'rose';
    return 'green';
  };

  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginTop: 20 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13.5, fontWeight: 600 }}>Organization mailbox access</div>
        <div style={{ flex: 1 }} />
        <label style={{ fontSize: 11.5, color: 'var(--text-3)', display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          include departed members
        </label>
        <Btn size="sm" variant="accent" onClick={connectAll} disabled={!granted || busy !== null}>
          {busy === 'all' ? 'Connecting…' : 'Connect everyone not connected'}
        </Btn>
      </div>
      {status === null && <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 10 }}>Checking the grant…</div>}
      {status && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 10 }}>
          {['Mail.Read', 'Calendars.Read'].map((r) => (
            <Pill key={r} hue={status.roles.includes(r) ? 'green' : 'rose'} dot sz="sm">
              {r} {status.roles.includes(r) ? 'granted' : 'missing'}
            </Pill>
          ))}
          {!status.configured && <Pill hue="rose" sz="sm">Microsoft app not configured on the server</Pill>}
          {status.error && <span style={{ fontSize: 11.5, color: 'var(--rose-fg)' }}>{status.error}</span>}
        </div>
      )}
      {status && rows.length > 0 && (
        <div style={{ marginTop: 12, border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
          {rows.map((m, i) => (
            <div key={m.member_id} style={{
              display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 10, alignItems: 'center',
              padding: '7px 10px', borderTop: i ? '1px solid var(--border)' : 'none',
              opacity: m.member_active ? 1 : 0.7,
            }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 500 }}>
                  {m.name}{!m.member_active && <span style={{ color: 'var(--text-4)', fontWeight: 400 }}> · departed</span>}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-3)', fontFamily: 'var(--font-mono)' }}>
                  {m.email}{m.last_sync_error ? ' · sync failing' : ''}
                </div>
              </div>
              <Pill hue={rowHue(m)} sz="sm">{modeLabel[m.mode] || m.mode}</Pill>
              <Btn size="sm" variant="outline" onClick={() => connect(m)}
                disabled={!granted || busy !== null || (m.mode === 'application' && m.credential_active && !m.last_sync_error)}>
                {busy === m.member_id ? 'Connecting…' : (m.mode === 'application' ? 'Reconnect' : 'Connect via organization')}
              </Btn>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Live sync-status line on each connected card. Three states:
//   1. "Syncing now…" — last_sync_started_at exists and is newer than
//      last_synced_at. Polls the credential list — every 4s at first, backing
//      off as the sync drags on — so the user sees results land without
//      manually refreshing.
//   2. "Last sync failed: <error>" — last_sync_error is set.
//   3. "Last sync: 12 new, 3 updated" — last_sync_summary populated.
//   4. (nothing) — never run.
const SyncStatusLine = ({ cred, onTick }) => {
  const started  = cred.last_sync_started_at ? new Date(cred.last_sync_started_at) : null;
  const finished = cred.last_synced_at ? new Date(cred.last_synced_at) : null;
  // Boolean, not the truthy Date — it gates the poll effect below.
  const inFlight = !!(started && (!finished || started > finished));

  // `onTick` reloads the credential list on the parent, so there's no result
  // to fingerprint here — the ladder just climbs while the sync runs, and
  // `inFlight` going false tears the poll down.
  window.usePolling(onTick, { active: inFlight, first: 4000 });

  if (inFlight) {
    return (
      <div style={{ marginTop: 4, fontSize: 11, color: 'var(--amber-fg)', display: 'flex',
                    alignItems: 'center', gap: 6 }}>
        <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 4,
                       background: 'var(--amber-fg)' }} />
        Syncing now… started {Math.max(0, Math.round((Date.now() - started.getTime()) / 1000))}s ago
      </div>
    );
  }
  if (cred.last_sync_error) {
    return (
      <div style={{ marginTop: 4, fontSize: 11, color: 'var(--rose-fg)' }}>
        ⚠ Last sync failed: {cred.last_sync_error}
      </div>
    );
  }
  if (cred.last_sync_summary) {
    const s = cred.last_sync_summary;
    // Outlook nests per-stream counts ({calendar:{…}, mail:{…}}); Fireflies is
    // flat ({created, updated, skipped}). Render whichever shape we got.
    const cal  = s.calendar && typeof s.calendar === 'object' ? s.calendar : null;
    const mail = s.mail && typeof s.mail === 'object' ? s.mail : null;
    let line;
    if (cal || mail) {
      const plural = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`;
      const bits = [];
      if (cal)  bits.push(plural((cal.created || 0)  + (cal.updated || 0),  'meeting'));
      if (mail) bits.push(plural((mail.created || 0) + (mail.updated || 0), 'email'));
      line = `Last sync: ${bits.join(', ') || 'nothing new'}`;
    } else {
      line = `Last sync: ${s.created || 0} new, ${s.updated || 0} updated`
           + (s.skipped > 0 ? `, ${s.skipped} skipped` : '');
    }
    return (
      <div style={{ marginTop: 4, fontSize: 11, color: 'var(--text-3)' }}>
        {line}
        {s.window_days ? ` (last ${s.window_days >= 3000 ? 'all time' : s.window_days + ' days'})` : ''}
      </div>
    );
  }
  return null;
};

// "Webhook last delivered N ago" — the other clock on a connected card.
//
// SyncStatusLine above shows the hourly sweep. The webhook is a separate path
// that cannot touch the sweep's watermark (a targeted probe must not advance
// it), and that guard used to mean a delivery left no trace at all. Fireflies
// webhook registration is PER USER, in each person's own dashboard, so when
// one lapses Atlas degrades from seconds to an hour with no error anywhere and
// the only observable difference is note-arrival latency. sync_fireflies.py
// now stamps last_sync_summary.webhook on a successful targeted ingest.
//
// Renders nothing when nothing has ever been delivered: we cannot tell a
// registration that was never made from an account that simply has not
// recorded a meeting yet, and telling a new user their setup is broken on
// their first day would be wrong. The daily data-quality digest carries the
// rollout picture ("N of M accounts have ever delivered"); this line carries
// the regression — a timestamp that has stopped moving.
const WEBHOOK_QUIET_HOURS = 72;   // matches WEBHOOK_STALE_HOURS in daily_data_quality.py

const WebhookStatusLine = ({ cred }) => {
  const wh = cred.last_sync_summary && cred.last_sync_summary.webhook;
  const at = wh && wh.last_delivered_at ? new Date(wh.last_delivered_at) : null;
  if (!at || isNaN(at.getTime())) return null;

  const hours = (Date.now() - at.getTime()) / 3600000;
  const quiet = hours >= WEBHOOK_QUIET_HOURS;
  const ago = hours < 1 ? `${Math.max(1, Math.round(hours * 60))}m ago`
            : hours < 48 ? `${Math.round(hours)}h ago`
            : `${Math.round(hours / 24)} days ago`;

  return (
    <div style={{ marginTop: 4, fontSize: 11,
                  color: quiet ? 'var(--amber-fg)' : 'var(--text-3)' }}>
      {quiet ? '\u26a0 ' : ''}Webhook: last delivery {ago}
      {quiet ? ' — meetings are arriving on the hourly sweep instead; re-register the webhook in Fireflies'
             : ''}
    </div>
  );
};

// Deep-history backfill state on each connected card. States:
//   queued  — waiting for the worker's next hourly tick
//   running — the deep pull is in flight (SyncStatusLine's poll covers refresh)
//   done    — "History synced from <date>" (the depth guarantee)
//   error   — failed; the Backfill button re-queues
// Plus a truncation nudge when the last regular sync hit its item cap and
// silently dropped the oldest slice (the Zach-hit-exactly-2000 case).
const BackfillStatusLine = ({ cred }) => {
  const fmtDate = (iso) => iso ? new Date(iso + 'T00:00:00').toLocaleDateString() : '';
  // backfill_until is an exclusive end — absent means "up to now".
  const through = cred.backfill_until ? ` (up to ${fmtDate(cred.backfill_until)})` : '';
  const lines = [];
  if (cred.backfill_status === 'queued') {
    lines.push(
      <div key="bf" style={{ marginTop: 4, fontSize: 11, color: 'var(--text-3)' }}>
        Full-history backfill queued (back to {fmtDate(cred.backfill_since)}{through}) — runs with the next hourly sync
      </div>
    );
  } else if (cred.backfill_status === 'running') {
    lines.push(
      <div key="bf" style={{ marginTop: 4, fontSize: 11, color: 'var(--amber-fg)', display: 'flex',
                             alignItems: 'center', gap: 6 }}>
        <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 4,
                       background: 'var(--amber-fg)' }} />
        Backfilling history back to {fmtDate(cred.backfill_since)}{through}…
      </div>
    );
  } else if (cred.backfill_status === 'error') {
    lines.push(
      <div key="bf" style={{ marginTop: 4, fontSize: 11, color: 'var(--rose-fg)' }}>
        ⚠ History backfill failed: {cred.backfill_error || 'unknown error'} — use "Backfill full history" to retry
      </div>
    );
  } else if (cred.backfill_status === 'done' && cred.backfill_since) {
    lines.push(
      <div key="bf" style={{ marginTop: 4, fontSize: 11, color: 'var(--text-3)' }}>
        History synced from {fmtDate(cred.backfill_since)}{through}
      </div>
    );
  }
  if (cred.last_sync_truncated && cred.backfill_status !== 'queued' && cred.backfill_status !== 'running') {
    lines.push(
      <div key="trunc" style={{ marginTop: 4, fontSize: 11, color: 'var(--amber-fg)' }}>
        ⚠ Last sync hit its item cap — older history may be missing. "Backfill full history" pulls the rest.
      </div>
    );
  }
  return lines.length ? <>{lines}</> : null;
};

// One capability row — used in both the Connect-drawer preview AND the
// connected-card toggle panel. Same component, just different `enabled`
// + `onToggle` source.
const FeatureRow = ({ feature, enabled, onToggle }) => {
  const disabled = feature.required || feature.status !== 'ready';
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
      <button
        onClick={() => !disabled && onToggle(!enabled)}
        disabled={disabled}
        style={{
          width: 30, height: 18, borderRadius: 9, position: 'relative',
          background: enabled ? 'var(--green-fg)' : 'var(--border-strong)',
          border: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled && !enabled ? 0.4 : 1, flexShrink: 0, marginTop: 2,
        }}
        title={feature.required ? 'Required — core feature' :
               feature.status !== 'ready' ? 'Coming soon — toggle will unlock when shipped' : ''}>
        <span style={{
          position: 'absolute', top: 2, left: enabled ? 14 : 2,
          width: 14, height: 14, borderRadius: '50%', background: '#fff',
          transition: 'left .12s',
        }} />
      </button>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5,
                      color: disabled && !enabled ? 'var(--text-3)' : 'var(--text)',
                      fontWeight: 500 }}>
          {feature.label}
          {feature.required && <Pill hue="slate" sz="sm">Required</Pill>}
          {feature.status === 'coming_soon' && <Pill hue="amber" sz="sm">Coming soon</Pill>}
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 2, lineHeight: 1.5 }}>
          {feature.description}
        </div>
      </div>
    </div>
  );
};

// ============ FEATURE FLAGS ============
// Team-wide switches (admin), each saved at once. "New record pages" is the
// team's record layout (app_settings 'record_layout' through PUT
// /api/auth/record-layout, whoami.record_layout for everyone); the rest come
// from GET /api/auth/feature-flags (app/feature_flags.py).
const FlagRow = ({ label, description, on, saving, onToggle }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '14px 0', borderBottom: '1px solid var(--border)' }}>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{label}</div>
      {description && <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 2, lineHeight: 1.5 }}>{description}</div>}
    </div>
    <button role="switch" aria-checked={on} aria-label={label} disabled={saving} onClick={() => onToggle(!on)}
      style={{
        width: 30, height: 18, borderRadius: 9, position: 'relative', flexShrink: 0, marginTop: 2,
        background: on ? 'var(--green-fg)' : 'var(--border-strong)',
        border: 'none', cursor: saving ? 'progress' : 'pointer', opacity: saving ? 0.6 : 1,
      }}>
      <span style={{
        position: 'absolute', top: 2, left: on ? 14 : 2,
        width: 14, height: 14, borderRadius: '50%', background: '#fff',
        transition: 'left .12s',
      }} />
    </button>
  </div>
);

const RecordLayoutField = () => {
  const team = () => { try { return (window.recordLayoutState && window.recordLayoutState().team) || 'v2'; } catch (_) { return 'v2'; } };
  const [layout, setLayout] = React.useState(team);
  const [saving, setSaving] = React.useState(false);
  const picked = React.useRef(false);
  // This tab's copy dates from its boot: another tab (or admin) may have changed
  // it since, so read it fresh (never whoami's 60 s cache) unless a pick came first.
  React.useEffect(() => {
    let alive = true;
    window.AtlasApi.whoami({ fresh: true }).then((me) => {
      if (!alive || picked.current || !me || (me.record_layout !== 'v2' && me.record_layout !== 'classic')) return;
      setLayout(me.record_layout);
      window.setRecordTeamLayout(me.record_layout, { admin: (me.role || '').toLowerCase() === 'admin' });
    }).catch(() => {});
    return () => { alive = false; };
  }, []);
  const pick = async (next) => {
    if (saving || !next || next === layout) return;
    picked.current = true;
    const prev = layout;
    setLayout(next);
    setSaving(true);
    try {
      const res = await window.AtlasApi.setRecordLayout(next);
      const saved = (res && res.layout) || next;
      setLayout(saved);
      window.setRecordTeamLayout(saved, { admin: true, dropPreview: true });
      pushToast({ type: 'success', title: saved === 'v2' ? 'New record pages: on' : 'New record pages: off' });
    } catch (e) {
      setLayout(prev);
      pushToast({ type: 'error', title: "Couldn't change the record layout", body: e.message });
    } finally {
      setSaving(false);
    }
  };
  return (
    <FlagRow label="New record pages" description="The redesigned company, contact and deal pages. Off = the classic pages."
      on={layout === 'v2'} saving={saving} onToggle={(on) => pick(on ? 'v2' : 'classic')} />
  );
};

const FeatureFlagsSettings = () => {
  const [flags, setFlags] = React.useState([]);
  const [saving, setSaving] = React.useState(null);
  React.useEffect(() => {
    let alive = true;
    window.AtlasApi.featureFlags().then((r) => { if (alive) setFlags((r && r.flags) || []); })
      .catch((e) => { if (alive) pushToast({ type: 'error', title: "Couldn't load feature flags", body: e.message }); });
    return () => { alive = false; };
  }, []);
  const toggle = async (flag, on) => {
    if (saving) return;
    setSaving(flag.key);
    setFlags((fs) => fs.map((f) => (f.key === flag.key ? { ...f, on } : f)));
    try {
      const r = await window.AtlasApi.setFeatureFlag(flag.key, on);
      if (r && r.flags) setFlags(r.flags);
      pushToast({ type: 'success', title: `${flag.label}: ${on ? 'on' : 'off'}` });
    } catch (e) {
      setFlags((fs) => fs.map((f) => (f.key === flag.key ? { ...f, on: !on } : f)));
      pushToast({ type: 'error', title: `Couldn't change ${flag.label}`, body: e.message });
    } finally {
      setSaving(null);
    }
  };
  return (
    <>
      <SettingsHeader title="Feature flags" />
      <div style={{ padding: '6px 28px 20px', maxWidth: 640 }}>
        <RecordLayoutField />
        {flags.map((f) => (
          <FlagRow key={f.key} label={f.label} description={f.description} on={!!f.on}
            saving={saving === f.key} onToggle={(on) => toggle(f, on)} />
        ))}
      </div>
    </>
  );
};

// ============ GENERAL ============
// Workspace name + accent + density (localStorage, behind Save changes).
const GeneralSettings = () => {
  const [name, setName] = React.useState(() => localStorage.getItem('atlas.workspaceName') || 'IndustryPro');
  const [accent, setAccent] = React.useState(() => localStorage.getItem('atlas.accent') || 'atlas');
  const [density, setDensity] = React.useState(() => localStorage.getItem('atlas.density') || 'comfortable');
  const save = () => {
    localStorage.setItem('atlas.workspaceName', name);
    localStorage.setItem('atlas.accent', accent);
    localStorage.setItem('atlas.density', density);
    pushToast({ type: 'success', title: 'Workspace settings saved' });
  };
  return (
    <>
      <SettingsHeader title="General" />
      <div style={{ padding: '20px 28px', maxWidth: 640, display: 'flex', flexDirection: 'column', gap: 18 }}>
        <Field label="Workspace name">
          <TextInput value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Accent color">
          <Combobox value={accent} onChange={(e) => setAccent(e.target.value)}
            options={[
              { value: 'atlas',  label: 'Atlas (blue)' },
              { value: 'indigo', label: 'Indigo' },
              { value: 'forest', label: 'Forest' },
              { value: 'ember',  label: 'Ember' },
            ]} />
        </Field>
        <Field label="Default row density">
          <Combobox value={density} onChange={(e) => setDensity(e.target.value)}
            options={[
              { value: 'compact',     label: 'Compact (32px rows)' },
              { value: 'comfortable', label: 'Comfortable (36px rows)' },
              { value: 'spacious',    label: 'Spacious (40px rows)' },
            ]} />
        </Field>
        <div>
          <Btn size="sm" variant="accent" icon={<Icon.Check size={12} />} onClick={save}>Save changes</Btn>
        </div>
      </div>
    </>
  );
};

// ============ OBJECTS ============
// Live objects backed by real Postgres tables. Counts come from /api/dashboard/counts.
const OBJECT_TYPES = [
  { id: 'company',    label: 'Companies',   countKey: 'companies',   icon: 'Building', tone: 'teal'   },
  { id: 'contact',    label: 'Contacts',    countKey: 'contacts',    icon: 'User',     tone: 'violet' },
  { id: 'engagement', label: 'Engagements', countKey: 'engagements', icon: 'Diamond',  tone: 'blue'   },
];


const ObjectsSettings = () => {
  const [obj, setObj] = React.useState('company');
  const [counts, setCounts] = React.useState(null);
  const [defs, setDefs] = React.useState(null);
  const [editing, setEditing] = React.useState(null);
  const [creating, setCreating] = React.useState(false);
  // Identity keys (read-only registry) — which normalized fields drive
  // duplicate detection for each object. Defined in code because every key
  // needs a normalizer + expression index; the panel makes them visible.
  const [identityKeys, setIdentityKeys] = React.useState(null);

  React.useEffect(() => {
    window.AtlasApi.identityKeys()
      .then((k) => setIdentityKeys(Array.isArray(k) ? k : []))
      .catch(() => setIdentityKeys([]));
  }, []);

  const reload = React.useCallback(async () => {
    const [c, d] = await Promise.all([
      window.AtlasApi.dashboardCounts().catch(() => null),
      window.AtlasApi.listFieldDefinitions(obj).catch(() => []),
    ]);
    setCounts(c);
    setDefs(Array.isArray(d) ? d : []);
  }, [obj]);

  React.useEffect(() => { reload(); }, [reload]);

  const current = OBJECT_TYPES.find((o) => o.id === obj);

  return (
    <>
      <SettingsHeader title="Objects" />

      <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', minHeight: 'calc(100% - 92px)' }}>
        <div style={{ borderRight: '1px solid var(--border)', background: 'var(--surface)', padding: '10px 0' }}>
          {OBJECT_TYPES.map((o) => {
            const Ic = Icon[o.icon] || Icon.Folder;
            const tone = HUE[o.tone];
            const a = obj === o.id;
            const recordCount = counts ? counts[o.countKey] : null;
            return (
              <button key={o.id}
                onClick={() => { setObj(o.id); setEditing(null); setCreating(false); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '8px 18px',
                  border: 'none', cursor: 'pointer', textAlign: 'left',
                  background: a ? 'var(--surface-hover)' : 'transparent',
                  borderLeft: a ? '2px solid var(--accent)' : '2px solid transparent',
                }}
                onMouseEnter={(e) => { if (!a) e.currentTarget.style.background = 'var(--surface-hover)'; }}
                onMouseLeave={(e) => { if (!a) e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{ width: 24, height: 24, borderRadius: 6, background: tone.bg, color: tone.fg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Ic size={13} />
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>{o.label}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-3)' }} className="num">
                    {recordCount != null ? `${recordCount.toLocaleString()} records` : '—'}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <div style={{ padding: '20px 28px', overflow: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div>
              <h2 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)' }}>{current.label}</h2>
              <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 1 }}>API name <span className="mono">{current.id}</span></div>
            </div>
            <Btn size="sm" variant="accent" icon={<Icon.Plus size={12} />} onClick={() => setCreating(true)}>Add field</Btn>
          </div>

          {(() => {
            const keys = (identityKeys || []).filter((k) => k.object === obj);
            if (!keys.length) return null;
            return (
              <div style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                  Identity — drives duplicate detection
                </div>
                <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 9, overflow: 'hidden' }}>
                  {keys.map((k, i) => (
                    <div key={k.key} style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px',
                      borderTop: i === 0 ? 'none' : '1px solid var(--border)', fontSize: 12,
                    }}>
                      <span style={{ fontWeight: 600, width: 150, flexShrink: 0 }}>{k.label}</span>
                      <span className="mono" style={{ fontSize: 10.5, color: 'var(--text-4)', width: 170, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{k.source}</span>
                      <span style={{ flex: 1, minWidth: 0, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={k.normalizer}>{k.normalizer}</span>
                      {k.fuzzy && <Pill hue="violet" sz="sm">+ fuzzy</Pill>}
                      <Pill hue={k.tier === 'strong' ? 'rose' : 'amber'} sz="sm">{k.tier === 'strong' ? 'strong match' : 'needs review'}</Pill>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {defs === null && <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>}
          {defs !== null && defs.length === 0 && (
            <div style={{ fontSize: 12, color: 'var(--text-3)', padding: '20px 0' }}>No fields yet — click "Add field" to define one.</div>
          )}
          {defs && defs.length > 0 && (
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 9, overflow: 'hidden' }}>
              <div style={{ display: 'flex', height: 28, background: 'var(--surface-2)', fontSize: 10.5, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid var(--border)' }}>
                <div style={{ flex: 1.6, padding: '0 14px', display: 'flex', alignItems: 'center' }}>Field</div>
                <div style={{ width: 110, padding: '0 10px', display: 'flex', alignItems: 'center' }}>Type</div>
                <div style={{ width: 130, padding: '0 10px', display: 'flex', alignItems: 'center' }}>Source</div>
                <div style={{ flex: 1.4, padding: '0 10px', display: 'flex', alignItems: 'center' }}>Description</div>
                <div style={{ width: 60 }} />
              </div>
              {defs.map((f) => (
                <div key={f.id}
                  onClick={() => setEditing(f)}
                  style={{
                    display: 'flex', minHeight: 40, alignItems: 'center', cursor: 'pointer',
                    borderBottom: '1px solid var(--border)',
                    opacity: f.active ? 1 : 0.5,
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-2)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{ flex: 1.6, padding: '8px 14px' }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>{f.display_name}</div>
                    <div className="mono" style={{ fontSize: 10.5, color: 'var(--text-4)' }}>{f.field_key}</div>
                  </div>
                  <div style={{ width: 110, padding: '0 10px' }}>
                    <Pill hue="slate" sz="sm">{f.field_type || 'text'}</Pill>
                  </div>
                  <div style={{ width: 130, padding: '0 10px' }}>
                    <Pill hue={
                      f.fill_mode === 'imported'   ? 'teal'   :
                      f.fill_mode === 'web_search' ? 'violet' :
                      f.fill_mode === 'ai_prompt'  ? 'amber'  : 'slate'
                    } sz="sm">{f.fill_mode.replace('_', ' ')}</Pill>
                  </div>
                  <div style={{ flex: 1.4, padding: '0 10px', fontSize: 11.5, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {f.description || '—'}
                  </div>
                  <div style={{ width: 60, display: 'flex', justifyContent: 'flex-end', paddingRight: 14 }}>
                    <Icon.ChevronR size={12} color="var(--text-4)" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {(editing || creating) && (
        <FieldDefinitionDrawer
          field={editing}
          isNew={creating}
          objectType={obj}
          onClose={() => { setEditing(null); setCreating(false); }}
          onChange={async () => { await reload(); }}
        />
      )}
    </>
  );
};

// ============ PIPELINES ============
const PIPELINE_STAGE_HUE = {
  prospect: 'slate', qualified: 'slate', interested: 'teal',
  ds_call: 'teal', hot_lead: 'teal', blast: 'violet', bs_call: 'violet',
  data_gathering: 'amber', valuation_presented: 'amber',
  loi: 'blue', won: 'green',
  // Formal sell-side process (082) — hues mirror the equivalent buyside step.
  ss_prospect: 'slate', ss_qualified: 'slate', ss_interested: 'teal',
  ss_valuation: 'amber', ss_pitch: 'teal', ss_cim_prep: 'amber',
  ss_buyer_outreach: 'violet', ss_ioi: 'blue', ss_loi: 'blue',
  ss_mgmt_presentation: 'blue', ss_won: 'green',
};

const PipelinesSettings = () => {
  const [stages, setStages] = React.useState(null);
  const [counts, setCounts] = React.useState({});
  const [editing, setEditing] = React.useState(null);
  const [creating, setCreating] = React.useState(false);

  const reload = React.useCallback(async () => {
    const [s, eng] = await Promise.all([
      window.AtlasApi.listStages().catch(() => []),
      window.AtlasApi.listEngagements({ limit: 1000, my_team: true }).catch(() => null),
    ]);
    setStages(Array.isArray(s) ? s : []);
    const items = (eng && eng.items) || [];
    const byStage = {};
    items.forEach((e) => { byStage[e.stage_slug] = (byStage[e.stage_slug] || 0) + 1; });
    setCounts(byStage);
  }, []);

  React.useEffect(() => { reload(); }, [reload]);

  if (stages === null) {
    return <SettingsHeader title="Pipelines" sub="Loading…" />;
  }

  const marketing = stages.filter((s) => s.category === 'marketing');
  const dealmaker = stages.filter((s) => s.category === 'dealmaker');
  const sellside  = stages.filter((s) => s.category === 'sellside');

  const StageCard = ({ s }) => {
    const hue = HUE[PIPELINE_STAGE_HUE[s.slug] || 'slate'];
    const n = counts[s.slug] || 0;
    return (
      <button onClick={() => setEditing(s)} style={{
        minWidth: 170, padding: '10px 12px', background: hue.bg,
        border: `1px solid ${hue.dot}`, borderRadius: 8,
        cursor: 'pointer', textAlign: 'left',
        transition: 'box-shadow .12s, transform .12s',
      }}
        onMouseEnter={(e) => { e.currentTarget.style.boxShadow = 'var(--shadow-sm)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 6, height: 6, borderRadius: 999, background: hue.dot }} />
          <span style={{ fontSize: 12, fontWeight: 600, color: hue.fg, flex: 1 }}>{s.name}</span>
          {s.is_terminal && <Pill hue="green" sz="sm">terminal</Pill>}
        </div>
        <div style={{ fontSize: 11, color: hue.fg, opacity: 0.85, marginTop: 5, display: 'flex', justifyContent: 'space-between' }}>
          <span className="num">{s.probability_pct}% prob</span>
          <span className="num">{n} deals</span>
        </div>
        {s.exits.length > 0 && (
          <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${hue.dot}`, opacity: 0.95 }}>
            <div style={{ fontSize: 9.5, color: hue.fg, opacity: 0.75, textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600, marginBottom: 4 }}>Off-ramps</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              {s.exits.map((x) => (
                <span key={x.id} style={{
                  fontSize: 10, padding: '1px 6px', borderRadius: 4,
                  background: 'rgba(255,255,255,0.55)', color: hue.fg,
                }}>{x.name}</span>
              ))}
            </div>
          </div>
        )}
      </button>
    );
  };

  const AddStageButton = ({ category }) => (
    <button
      onClick={() => setCreating({ category })}
      style={{
        minWidth: 130, padding: '8px 10px', background: 'var(--surface-2)',
        border: '1px dashed var(--border-strong)', borderRadius: 7, cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
        fontSize: 11.5, color: 'var(--text-3)',
      }}
    >
      <Icon.Plus size={11} />Add stage
    </button>
  );

  return (
    <>
      <SettingsHeader title="Pipeline" />
      <div style={{ padding: '20px 28px', display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Marketing</span>
          </div>
          <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
            {marketing.map((s) => <StageCard key={s.slug} s={s} />)}
            <AddStageButton category="marketing" />
          </div>
        </div>

        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Dealmaker</span>
          </div>
          <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
            {dealmaker.map((s) => <StageCard key={s.slug} s={s} />)}
            <AddStageButton category="dealmaker" />
          </div>
        </div>

        {/* Formal sell-side process (082) — used by engagement_type='sell_side'
            deals. Its own family: probabilities here drive their weighted fees. */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Sell-side process</span>
          </div>
          <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
            {sellside.map((s) => <StageCard key={s.slug} s={s} />)}
            <AddStageButton category="sellside" />
          </div>
        </div>
      </div>

      {(editing || creating) && (
        <StageDrawer
          stage={editing}
          isNew={Boolean(creating)}
          defaultCategory={creating?.category || 'dealmaker'}
          nextOrder={(stages.length ? Math.max(...stages.map(s => s.order_index)) : 0) + 1}
          onClose={() => { setEditing(null); setCreating(false); }}
          onChange={async () => { await reload(); }}
        />
      )}
    </>
  );
};

const StageDrawer = ({ stage, isNew, defaultCategory, nextOrder, onClose, onChange }) => {
  const [form, setForm] = React.useState({
    name:            stage?.name || '',
    slug:            stage?.slug || '',
    category:        stage?.category || defaultCategory,
    order_index:     stage?.order_index ?? nextOrder,
    probability_pct: stage?.probability_pct ?? 0,
    is_terminal:     stage?.is_terminal ?? false,
    active:          stage?.active ?? true,
  });
  const [exits, setExits] = React.useState(stage?.exits || []);
  const [saving, setSaving] = React.useState(false);
  const [newExit, setNewExit] = React.useState('');
  const [newExitRecover, setNewExitRecover] = React.useState(true);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        slug: (form.slug || '').trim() || undefined,
        category: form.category,
        order_index: Number(form.order_index),
        probability_pct: Number(form.probability_pct),
        is_terminal: Boolean(form.is_terminal),
        active: Boolean(form.active),
      };
      if (isNew) {
        await window.AtlasApi.createStage(payload);
        pushToast({ type: 'success', title: 'Stage created' });
      } else {
        await window.AtlasApi.updateStage(stage.id, payload);
        pushToast({ type: 'success', title: 'Stage saved' });
      }
      onChange(); onClose();
    } catch (e) {
      pushToast({ type: 'error', title: 'Save failed', body: e.message });
    } finally { setSaving(false); }
  };

  const remove = async () => {
    const ok = await showConfirm({
      title: 'Delete this stage?',
      body: 'Engagements at this stage will keep their stage_id but the stage row goes away. Use deactivate to hide it instead.',
      confirmLabel: 'Delete', danger: true,
    });
    if (!ok) return;
    await window.AtlasApi.deleteStage(stage.id, true);
    pushToast({ type: 'success', title: 'Stage deleted' });
    onChange(); onClose();
  };

  const addExit = async () => {
    if (!newExit.trim() || isNew) return;
    try {
      const ex = await window.AtlasApi.createStageExit(stage.id, {
        name: newExit.trim(), is_recoverable: newExitRecover,
      });
      setExits((xs) => [...xs, ex]);
      setNewExit('');
      pushToast({ type: 'success', title: 'Exit added' });
    } catch (e) {
      pushToast({ type: 'error', title: 'Add failed', body: e.message });
    }
  };

  const removeExit = async (exitId) => {
    if (isNew) return;
    await window.AtlasApi.deleteStageExit(stage.id, exitId);
    setExits((xs) => xs.filter((x) => x.id !== exitId));
  };

  return (
    <Drawer
      open={true}
      onClose={onClose}
      title={isNew ? 'New stage' : form.name || stage.name}
      subtitle={`${form.category} · order ${form.order_index}`}
      width={520}
      footer={(
        <>
          {!isNew && <Btn size="sm" variant="ghost" onClick={remove} disabled={saving}>Delete</Btn>}
          <div style={{ flex: 1 }} />
          <Btn size="sm" variant="outline" onClick={onClose} disabled={saving}>Cancel</Btn>
          <Btn size="sm" variant="accent" onClick={save} disabled={saving || !form.name.trim()}>
            {saving ? 'Saving…' : (isNew ? 'Create' : 'Save')}
          </Btn>
        </>
      )}
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Field label="Name"><TextInput value={form.name} onChange={set('name')} /></Field>
        <Field label="Slug" hint={isNew ? 'Auto-derived if blank' : 'Used by API + URLs'}>
          <TextInput value={form.slug} onChange={set('slug')} />
        </Field>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
        <Field label="Category">
          <Combobox value={form.category} onChange={set('category')}
            options={[
              { value: 'marketing', label: 'Marketing' },
              { value: 'dealmaker', label: 'Dealmaker' },
              { value: 'sellside',  label: 'Sell-side process' },
            ]} />
        </Field>
        <Field label="Order">
          <TextInput type="number" value={form.order_index} onChange={set('order_index')} />
        </Field>
        <Field label="Probability %" hint="0–100">
          <TextInput type="number" min="0" max="100" value={form.probability_pct} onChange={set('probability_pct')} />
        </Field>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Field label="Terminal" hint="Marks the won/closed end of the pipeline">
          <Combobox value={String(form.is_terminal)} onChange={(e) => setForm((f) => ({ ...f, is_terminal: e.target.value === 'true' }))}
            options={[{ value: 'false', label: 'No' }, { value: 'true', label: 'Yes' }]} />
        </Field>
        <Field label="Active">
          <Combobox value={String(form.active)} onChange={(e) => setForm((f) => ({ ...f, active: e.target.value === 'true' }))}
            options={[{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }]} />
        </Field>
      </div>

      <div style={{ marginTop: 10, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <span style={{ fontSize: 10.5, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Off-ramps</span>
          <span style={{ fontSize: 10.5, color: 'var(--text-4)' }}>{exits.length}</span>
        </div>
        {isNew && (
          <div style={{ fontSize: 11, color: 'var(--text-3)', padding: 8, background: 'var(--surface-2)', borderRadius: 6 }}>
            Create the stage first, then add off-ramps.
          </div>
        )}
        {!isNew && (
          <>
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, marginBottom: 8 }}>
              {exits.length === 0 && (
                <div style={{ padding: '8px 10px', fontSize: 11.5, color: 'var(--text-4)' }}>No off-ramps yet.</div>
              )}
              {exits.map((x, i) => (
                <div key={x.id} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '6px 10px', borderTop: i === 0 ? 'none' : '1px solid var(--border)', fontSize: 12,
                }}>
                  <span style={{ flex: 1, color: 'var(--text)' }}>{x.name}</span>
                  <Pill hue={x.is_recoverable ? 'teal' : 'rose'} sz="sm">
                    {x.is_recoverable ? 'recoverable' : 'terminal'}
                  </Pill>
                  <button onClick={() => removeExit(x.id)} title="Delete" style={{
                    width: 22, height: 22, borderRadius: 4, background: 'transparent', border: 'none',
                    cursor: 'pointer', color: 'var(--text-4)', fontSize: 14,
                  }}>×</button>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <TextInput
                value={newExit}
                onChange={(e) => setNewExit(e.target.value)}
                placeholder="e.g. Buyer passed"
                onKeyDown={(e) => { if (e.key === 'Enter') addExit(); }}
              />
              <div style={{ width: 140, flexShrink: 0 }}>
                <Combobox value={String(newExitRecover)} onChange={(e) => setNewExitRecover(e.target.value === 'true')}
                  options={[
                    { value: 'true',  label: 'Recoverable' },
                    { value: 'false', label: 'Terminal' },
                  ]} />
              </div>
              <Btn size="sm" variant="outline" onClick={addExit} disabled={!newExit.trim()}>Add</Btn>
            </div>
          </>
        )}
      </div>
    </Drawer>
  );
};

window.SettingsView = SettingsView;

// Standalone view for the sidebar-promoted Integrations entry. Reuses
// IntegrationsSettings — activity now lives per-card so the page scales
// to many integrations without one provider's history dominating.
window.IntegrationsView = () => (
  <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
    <PageHeader
      breadcrumb={['Workspace', 'Integrations']}
      title="Integrations"
    />
    <div style={{ flex: 1, overflow: 'auto', background: 'var(--surface-2)' }}>
      <IntegrationsSettings />
    </div>
  </div>
);

// Activity panel — surfaces the linkage outcome of each ingested note.
// Three tabs: all, linked-with-method-breakdown, unlinked (with a
// "Link manually" inline picker).
const IntegrationActivityPanel = ({ provider }) => {
  const [tab, setTab] = React.useState('all'); // all | linked | unlinked
  const [data, setData] = React.useState(null);
  // Selected note for the modal — clicking any activity row opens it.
  const [openNoteId, setOpenNoteId] = React.useState(null);
  const reload = React.useCallback(() => {
    setData(null);
    window.AtlasApi.integrationActivity(provider, tab)
      .then(setData)
      .catch(() => setData({ items: [], total: 0, by_match: {} }));
  }, [provider, tab]);
  React.useEffect(() => { reload(); }, [reload]);

  return (
    <div>
      <div style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--text-4)',
                    textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
        Activity — what got synced
      </div>

      {/* Summary chip row */}
      {data && data.by_match && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
          {[
            ['email_direct',  'Email-direct',     'green'],
            ['domain',        'Domain fallback',  'teal'],
            ['title_fuzzy',   'Title fuzzy',      'amber'],
            ['auto_created',  'Contact auto-created', 'violet'],
            ['auto_created_company', 'Company auto-created', 'violet'],
            ['unlinked',      'Unlinked',         'rose'],
          ].map(([k, label, hue]) => (
            <span key={k} style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 500,
              background: `var(--${hue}-bg)`, color: `var(--${hue}-fg)`,
            }}>
              {label}
              <span style={{ opacity: 0.7, fontVariantNumeric: 'tabular-nums' }}>
                {data.by_match[k] || 0}
              </span>
            </span>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', marginBottom: 10 }}>
        {[
          ['all',      'All'],
          ['linked',   'Linked'],
          ['unlinked', 'Unlinked'],
        ].map(([k, label]) => {
          const active = tab === k;
          return (
            <button key={k} onClick={() => setTab(k)} style={{
              padding: '7px 12px', border: 'none', background: 'transparent',
              fontSize: 12.5, fontWeight: active ? 600 : 500, cursor: 'pointer',
              color: active ? 'var(--text)' : 'var(--text-3)',
              borderBottom: active ? '2px solid var(--text)' : '2px solid transparent',
              marginBottom: -1,
            }}>{label}</button>
          );
        })}
      </div>

      {data === null && <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>}
      {data && data.items.length === 0 && (
        <div style={{
          background: 'var(--surface)', border: '1px dashed var(--border-strong)', borderRadius: 10,
          padding: '32px 24px', fontSize: 12.5, color: 'var(--text-3)',
        }}>
          {tab === 'unlinked'
            ? 'Nothing waiting for review — every synced meeting matched to a company.'
            : 'No activity yet. Run "Sync now" to pull meetings.'}
        </div>
      )}

      {data && data.items.length > 0 && (
        <div style={{
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 10, overflow: 'hidden',
        }}>
          {data.items.map((it, i) => (
            <ActivityRow key={it.note_id} item={it} isFirst={i === 0} onReload={reload}
                          onOpen={() => setOpenNoteId(it.note_id)} />
          ))}
        </div>
      )}
      <NoteModal noteId={openNoteId} onClose={() => setOpenNoteId(null)} onChange={reload} />
    </div>
  );
};

const ActivityRow = ({ item, isFirst, onReload, onOpen }) => {
  const [showLinker, setShowLinker] = React.useState(false);
  const dateLabel = item.meeting_date
    ? new Date(item.meeting_date).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })
    : '—';
  // Keep linker-mode clicks from also opening the modal. Inline pills/links
  // already e.stopPropagation themselves.
  const handleRowClick = (e) => {
    if (showLinker) return;
    onOpen && onOpen();
  };
  const matchHue = {
    email_direct: 'green', domain: 'teal', title_fuzzy: 'amber',
    auto_created: 'violet', auto_created_company: 'violet', manual: 'blue',
  }[item.linked_via] || 'slate';

  return (
    <div
      onClick={handleRowClick}
      onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-2)'}
      onMouseLeave={(e) => e.currentTarget.style.background = ''}
      style={{
        padding: '12px 14px',
        borderTop: isFirst ? 'none' : '1px solid var(--border)',
        cursor: showLinker ? 'default' : 'pointer',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--text)', fontWeight: 600 }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {item.title || '(untitled meeting)'}
            </span>
            <span style={{ fontSize: 10.5, color: 'var(--text-4)', fontWeight: 400 }}>{dateLabel}</span>
          </div>

          <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: 11.5 }}>
            {item.company_name && item.company_id ? (
              // Wrap the company pill in a button-like span so clicking jumps
              // to the company detail view. Uses the global AtlasNav handle
              // exposed by app.jsx — keeps this self-contained view free of
              // prop drilling. stopPropagation so the row's onClick doesn't
              // also fire and open the note modal at the same time.
              <span
                role="button"
                onClick={(e) => { e.stopPropagation(); window.AtlasNav?.goToCompany(item.company_id); }}
                style={{ cursor: 'pointer' }}
                title="Open company"
              >
                <Pill hue="teal" sz="sm">🏢 {item.company_name}</Pill>
              </span>
            ) : item.company_name ? (
              <Pill hue="teal" sz="sm">🏢 {item.company_name}</Pill>
            ) : (
              <Pill hue="rose" sz="sm">no company</Pill>
            )}
            {item.contact_name && item.contact_id ? (
              <span
                role="button"
                onClick={(e) => { e.stopPropagation(); window.AtlasNav?.goToContact(item.contact_id); }}
                style={{ cursor: 'pointer' }}
                title="Open contact"
              >
                <Pill hue="slate" sz="sm">👤 {item.contact_name}</Pill>
              </span>
            ) : item.contact_name ? (
              <Pill hue="slate" sz="sm">👤 {item.contact_name}</Pill>
            ) : null}
            {item.linked_via && (
              <Pill hue={matchHue} sz="sm">via {item.linked_via.replace(/_/g, ' ')}</Pill>
            )}
            {/* Open in Fireflies → the Fireflies transcript page, not the
                Teams/Zoom join link (which is what external_url stores). The
                sync writes both into metadata; the activity endpoint now
                returns transcript_url as its own field. Fall back to
                external_url only when transcript_url is missing (legacy rows). */}
            {(item.transcript_url || item.external_url) && (
              <a href={item.transcript_url || item.external_url} target="_blank" rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{ fontSize: 10.5, color: 'var(--accent-text)', textDecoration: 'none' }}>
                Open in Fireflies ↗
              </a>
            )}
          </div>

          {(item.all_emails || []).length > 0 && (
            <div style={{ marginTop: 4, fontSize: 10.5, color: 'var(--text-4)' }}>
              Attendees: {item.all_emails.join(', ')}
            </div>
          )}

          {/* AI's best guess for unlinked meetings — shown only when the
              dedup matcher missed and Claude managed to extract a company
              name from the title/summary. One-click "Use this" sends a
              fuzzy lookup through Atlas and (if found) links the note. */}
          {!item.linked && item.ai_inference && item.ai_inference.likely_target_company && (
            <div style={{ marginTop: 6, padding: '6px 10px', background: 'var(--accent-soft)',
                          borderRadius: 5, fontSize: 11.5, color: 'var(--accent-text)',
                          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span>🤖 Claude's guess:</span>
              <span style={{ fontWeight: 600 }}>{item.ai_inference.likely_target_company}</span>
              <Pill hue="slate" sz="sm">{item.ai_inference.confidence}</Pill>
              {item.ai_inference.reason && (
                <span style={{ fontSize: 10.5, color: 'var(--text-3)', fontStyle: 'italic' }}>
                  — {item.ai_inference.reason}
                </span>
              )}
            </div>
          )}
        </div>

        {!item.linked && (
          <Btn size="xs" variant="outline"
                onClick={(e) => { e.stopPropagation(); setShowLinker((v) => !v); }}>
            {showLinker ? 'Cancel' : 'Link manually'}
          </Btn>
        )}
      </div>

      {showLinker && (
        // Wrap so type-ahead clicks don't fire the row's open-modal handler.
        <div onClick={(e) => e.stopPropagation()}>
          <ManualLinkPicker noteId={item.note_id} onLinked={() => { setShowLinker(false); onReload(); }} />
        </div>
      )}
    </div>
  );
};

// Small inline picker for the unlinked queue — type-ahead against companies
// to pick the right counterparty, hit Save, the matcher records it and the
// row vanishes from the Unlinked tab.
const ManualLinkPicker = ({ noteId, onLinked }) => {
  const [q, setQ] = React.useState('');
  const [results, setResults] = React.useState([]);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (q.length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      window.AtlasApi.listCompanies({ q, limit: 8 })
        .then((r) => setResults((r?.items || []).slice(0, 8)))
        .catch(() => setResults([]));
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  const link = async (co) => {
    setSaving(true);
    try {
      await window.AtlasApi.manuallyLinkNote(noteId, { company_id: co.id });
      pushToast({ type: 'success', title: `Linked to ${co.name}` });
      onLinked();
    } catch (e) {
      pushToast({ type: 'error', title: 'Link failed', body: e.message });
    } finally { setSaving(false); }
  };

  return (
    <div style={{ marginTop: 10, padding: '10px 12px', background: 'var(--surface-2)',
                  border: '1px solid var(--border)', borderRadius: 6 }}>
      <input value={q} onChange={(e) => setQ(e.target.value)}
        autoFocus placeholder="Search companies to link…"
        style={{ width: '100%', padding: '5px 8px', fontSize: 12.5,
                 border: '1px solid var(--border)', borderRadius: 5, fontFamily: 'inherit' }} />
      {results.length > 0 && (
        <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {results.map((co) => (
            <button key={co.id} onClick={() => link(co)} disabled={saving}
              style={{
                textAlign: 'left', padding: '5px 8px', border: 'none', background: 'transparent',
                cursor: saving ? 'wait' : 'pointer', fontSize: 12, borderRadius: 4,
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
              <span style={{ fontWeight: 500, color: 'var(--text)' }}>{co.name}</span>
              {co.industry_name && <span style={{ color: 'var(--text-4)', marginLeft: 6 }}>· {co.industry_name}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
Object.assign(window, { ObjectsSettings, PipelinesSettings, SettingsHeader });
